import { SCOPED, SECTION_REPORT, PROPERTY_TYPES } from "./analytics";
import { SECTION_OPTION_SEGMENT } from "./segment";

const CRM_CLICK_IDS = ['gclid', 'fbclid', '_fbp', '_fbc', 'msclkid', 'li_fat_id', 'ttclid', 'dclid', 'srsltid', '_ga', 'calltrk_user'];

const CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS = CRM_CLICK_IDS.flatMap((name) => ['First', 'Last', 'Path'].map((type) => ({ name, type }))).map((item) => ({
     value: `created${item.name}${item.type}`,
     label: `Created by ${item.name} (${item.type})`,
     section: SECTION_OPTION_SEGMENT.CREATED_BY,
     sectionParent: SECTION_OPTION_SEGMENT.CLICK_IDS,
}));

const CRM_IMPORTED_CLICK_ID_PROPERTY_OPTIONS = CRM_CLICK_IDS.flatMap((name) => ['Path'].map((type) => ({ name, type }))).map((item) => ({
     value: `${item.name}PathImported]`,
     label: `Imported ${item.name} (path)`,
     section: SECTION_OPTION_SEGMENT.IMPORTED,
     sectionParent: SECTION_OPTION_SEGMENT.CLICK_IDS,
}));

export const DESTINATION_TYPE = {
     CLIENT_SIDE: 'clientSide',
     SERVER_SIDE: 'serverSide'
};

export const DATA_ACTION_TEMPLATE = {
     WEBHOOK: 'webhook',
     REVINATE: 'revinate',
     SALESFORCE: 'salesforce',
     KLAVIYO: 'klaviyo',
};

export const DATA_ACTION_SERVER_SIDE_TEMPLATE = [
     { value: DATA_ACTION_TEMPLATE.WEBHOOK, label: 'Webhook (Catch Hook)' },
     { value: DATA_ACTION_TEMPLATE.REVINATE, label: 'Revinate Template' },
     { value: DATA_ACTION_TEMPLATE.SALESFORCE, label: 'Salesforce Template' },
     { value: DATA_ACTION_TEMPLATE.KLAVIYO, label: 'Klaviyo' },
];

// Rollout gate for the Salesforce Data Action template. Nothing on the server enforces
// this — this list is the only thing limiting who can create one.
export const SALESFORCE_DATA_ACTION_ACCOUNT_IDS = [
     'd1e08572-afbd-43cf-982a-30f8b988a971',
     'dcf4fd9d-f190-4907-b83d-e11626c34f0d',
     'f8a3df73-0b49-4bc1-bd30-933e90279d29',
     'e19758b6-e7c7-412f-8ff9-4c27793e0c00', // staging test account
];

// currentTemplate keeps the option visible for a rule that is already on the Salesforce
// template even if its account is not on the list — otherwise the select matches nothing,
// renders blank, and writes an empty template back over a working rule.
export const getDataActionTemplateOptions = ({ accountId, currentTemplate } = {}) =>
     DATA_ACTION_SERVER_SIDE_TEMPLATE.filter(
          ({ value }) =>
               value !== DATA_ACTION_TEMPLATE.SALESFORCE ||
               SALESFORCE_DATA_ACTION_ACCOUNT_IDS.includes(accountId) ||
               currentTemplate === DATA_ACTION_TEMPLATE.SALESFORCE
     );

export const REVINATE_DEFAULT_CONTACTS_KEY = 'contacts';

export const REVINATE_ROOT_GROUP = {
     PROPERTIES: 'Properties',
     METRICS: 'Metrics',
};

export const REVINATE_FIELD_TYPE = {
     PROPERTY: 'property',
     METRIC: 'metric',
};

// Field names that land on Klaviyo's own profile attributes. Any other name the user types becomes
// a custom property. Must match KLAVIYO_PROFILE_ATTRIBUTES / KLAVIYO_LOCATION_ATTRIBUTES in
// server/utils/klaviyo.js and the Lambda's utils/klaviyo.mjs.
export const KLAVIYO_STANDARD_FIELDS = [
     'email', 'first_name', 'last_name', 'phone_number', 'title', 'organization', 'external_id',
     'location.city', 'location.region', 'location.country', 'location.zip',
];

// The Salesforce Data Action template maps page views, so its ListenLayer side is
// fixed to the four columns SendPeoplePageViewToZapier's buildQuery returns. There
// is no property picker. Keep the keys in sync with LL_FIELD in
// server/utils/salesforceDataAction.js and the lambda's salesforce/record.mjs.
export const SALESFORCE_LL_FIELD = {
     PRIMARY_EMAIL: 'primaryEmail',
     PAGE_VIEW_PATH: 'pageViewPath',
     PAGE_VIEW_TIMESTAMP: 'pageViewTimestamp',
     LAST_NAME: 'lastName',
};

export const SALESFORCE_LL_FIELD_OPTIONS = [
     { value: SALESFORCE_LL_FIELD.PRIMARY_EMAIL, label: 'Primary Email' },
     { value: SALESFORCE_LL_FIELD.PAGE_VIEW_PATH, label: 'Page View Path' },
     { value: SALESFORCE_LL_FIELD.PAGE_VIEW_TIMESTAMP, label: 'Page View Timestamp' },
     { value: SALESFORCE_LL_FIELD.LAST_NAME, label: 'Last Name' },
];

// Maps a sample row (which uses the lambda's column names) onto the labels above.
export const SALESFORCE_PREVIEW_COLUMNS = [
     { key: 'email', label: 'Primary Email' },
     { key: 'lastName', label: 'Last Name' },
     { key: 'pathName', label: 'Page View Path' },
     { key: 'time', label: 'Page View Timestamp' },
];

// One record is created per page view, so the source is fixed.
export const SALESFORCE_CREATE_FROM_LABEL = 'Page View Paths';

export const SALESFORCE_MATCH_OBJECT = 'Lead';
export const SALESFORCE_DEFAULT_MATCH_LL_FIELD = SALESFORCE_LL_FIELD.PRIMARY_EMAIL;
export const SALESFORCE_DEFAULT_MATCH_SF_FIELD = 'Email';

// Only a lookup/master-detail field can hold a Lead Id.
export const SALESFORCE_REFERENCE_TYPE = 'reference';

export const SEVER_SIDE_OBJECT = {
     LEAD: "Lead",
     CONTACT: "Contact",
     ACCOUNT: "Account",
     PARENT_ACCOUNT: "ParentAccount",
     OPPORTUNITY: "Opportunity",
     QUOTE: "Quote",
     ORDER: "Order",
     CONTRACT: "Contract",
     CAMPAIGN: "Campaign",
     EVENT: "Event",
     PEOPLE: "People",
     COMPANY: "Company",
     SALES_CONVERSION_EVENT: 'Sales Conversion Event'
};

export const MAPPING_CRM_TABLE = {
     [SEVER_SIDE_OBJECT.LEAD]: 'CRMLeads',
     [SEVER_SIDE_OBJECT.CONTACT]: 'CRMContacts',
     [SEVER_SIDE_OBJECT.ACCOUNT]: 'CRMAccounts',
     [SEVER_SIDE_OBJECT.PARENT_ACCOUNT]: 'CRMAccounts',
     [SEVER_SIDE_OBJECT.OPPORTUNITY]: 'CRMOpportunities',
     [SEVER_SIDE_OBJECT.QUOTE]: 'CRMQuotes',
     [SEVER_SIDE_OBJECT.ORDER]: 'CRMOrders',
     [SEVER_SIDE_OBJECT.CONTRACT]: 'CRMContracts',
     [SEVER_SIDE_OBJECT.CAMPAIGN]: 'CRMCampaigns',
     [SEVER_SIDE_OBJECT.EVENT]: 'CRMActivitiesEvents',
};

export const OBJECT_ID = {
     [SEVER_SIDE_OBJECT.PEOPLE]: 'personId',
     [SEVER_SIDE_OBJECT.COMPANY]: 'companyId',
     [SEVER_SIDE_OBJECT.LEAD]: 'leadID',
     [SEVER_SIDE_OBJECT.CONTACT]: 'contactID',
     [SEVER_SIDE_OBJECT.ACCOUNT]: 'accountID',
     [SEVER_SIDE_OBJECT.PARENT_ACCOUNT]: 'accountID',
     [SEVER_SIDE_OBJECT.OPPORTUNITY]: 'opportunityID',
     [SEVER_SIDE_OBJECT.QUOTE]: 'quoteID',
     [SEVER_SIDE_OBJECT.ORDER]: 'orderID',
     [SEVER_SIDE_OBJECT.CONTRACT]: 'contractID',
     [SEVER_SIDE_OBJECT.CAMPAIGN]: 'campaignID',
     [SEVER_SIDE_OBJECT.EVENT]: 'activityEventID',
     [SEVER_SIDE_OBJECT.SALES_CONVERSION_EVENT]: 'eventId',
}

export const MAPPING_PRIMARY_OBJECT_SCOPE = {
     [SEVER_SIDE_OBJECT.PEOPLE]: SCOPED.PEOPLE,
     [SEVER_SIDE_OBJECT.COMPANY]: SCOPED.COMPANY,
     [SEVER_SIDE_OBJECT.LEAD]: SCOPED.SF_LEAD,
     [SEVER_SIDE_OBJECT.CONTACT]: SCOPED.SF_CONTACT,
     [SEVER_SIDE_OBJECT.ACCOUNT]: SCOPED.SF_ACCOUNT,
     [SEVER_SIDE_OBJECT.PARENT_ACCOUNT]: SCOPED.SF_ACCOUNT,
     [SEVER_SIDE_OBJECT.OPPORTUNITY]: SCOPED.SF_OPPORTUNITY,
     [SEVER_SIDE_OBJECT.QUOTE]: SCOPED.SF_QUOTE,
     [SEVER_SIDE_OBJECT.ORDER]: SCOPED.SF_ORDER,
     [SEVER_SIDE_OBJECT.CONTRACT]: SCOPED.SF_CONTRACT,
     [SEVER_SIDE_OBJECT.CAMPAIGN]: SCOPED.SF_CAMPAIGN,
     [SEVER_SIDE_OBJECT.EVENT]: SCOPED.SF_EVENT,
     [SEVER_SIDE_OBJECT.SALES_CONVERSION_EVENT]: SCOPED.SALES_CONVERSION_EVENT,
};

export const DATE_FORMAT_OPTION = [
     // Simple Date
     {
          value: "isoDate",
          label: "ISO Date",
          section: 'Simple Date',
          example: '2025-07-31',
          description: 'Year-Month-Day format following ISO 8601, commonly used in databases and APIs for date-only data without time or timezone.',
          commonUses: 'Salesforce, General Databases, APIs',
          format: 'YYYY-MM-DD'
     },
     {
          value: "usDate",
          label: "US Date",
          section: "Simple Date",
          example: "07/31/2025",
          description: "Month/Day/Year format, widely used in US-centric systems for simple date representation.",
          commonUses: "General US-based systems",
          format: 'MM/DD/YYYY'
     },
     {
          value: "europeanDate",
          label: "European Date",
          section: "Simple Date",
          example: "31/07/2025",
          description: "Day/Month/Year format, common in Europe and other regions for simple date representation.",
          commonUses: "General European-based systems",
          format: 'DD/MM/YYYY'
     },
];
export const TIMESTAMP_FORMAT_OPTION = [
     // Simple Date
     {
          value: "isoDate",
          label: "ISO Date",
          section: 'Simple Date',
          example: '2025-07-31',
          description: 'Year-Month-Day format following ISO 8601, commonly used in databases and APIs for date-only data without time or timezone.',
          commonUses: 'Salesforce, General Databases, APIs',
          format: 'YYYY-MM-DD'
     },
     {
          value: "usDate",
          label: "US Date",
          section: "Simple Date",
          example: "07/31/2025",
          description: "Month/Day/Year format, widely used in US-centric systems for simple date representation.",
          commonUses: "General US-based systems",
          format: 'MM/DD/YYYY'
     },
     {
          value: "europeanDate",
          label: "European Date",
          section: "Simple Date",
          example: "31/07/2025",
          description: "Day/Month/Year format, common in Europe and other regions for simple date representation.",
          commonUses: "General European-based systems",
          format: 'DD/MM/YYYY'
     },

     // Detailed Date
     {
          value: "fullMonthTime",
          label: "Full Month Time",
          section: "Detailed Date",
          example: "July 31, 2025 12:34:56 UTC",
          description: "Human-readable format with full month name, time, and timezone, often used in logs or reports for clarity.",
          commonUses: "General logging, reporting systems",
          format: 'MMMM DD, YYYY HH:mm:ss z'
     },
     {
          value: "shortMonthTime",
          label: "Short Month Time",
          section: "Detailed Date",
          example: "Jul 31 2025 12:34:56",
          description: "Abbreviated month name with time, no timezone, used in simplified human-readable outputs.",
          commonUses: "General logging, reporting systems",
          format: 'MMM DD YYYY HH:mm:ss'
     },
     {
          value: "rfc2822",
          label: "RFC 2822",
          section: "Detailed Date",
          example: "Thu, 31 Jul 2025 12:34:56 +0000",
          description: "Standard for email and HTTP headers, includes day of week, abbreviated month, and timezone offset.",
          commonUses: "Email systems, HTTP APIs",
          format: 'EEE, DD MMM YYYY HH:mm:ss Z'

     },

     // Epoch-Based
     {
          value: "unixEpochSeconds",
          label: "Unix Epoch Seconds (UTC)",
          section: "Epoch-Based",
          example: "1753965296",
          description: "Seconds since January 1, 1970, UTC, used in Unix systems and many APIs for compact timestamp storage. Unix Epoch is always returns as UTC",
          commonUses: "Facebook Ads, Yahoo DSP, Liftoff, Pinterest, TikTok",
          format: 'Numeric'

     },
     {
          value: "jsEpochMilliseconds",
          label: "JavaScript Milliseconds (UTC)",
          section: "Epoch-Based",
          example: "1753965296000",
          description: "Milliseconds since January 1, 1970, UTC, used in JavaScript Date objects and web APIs. JavaScript Milliseconds are always returns as UTC",
          commonUses: "JavaScript-based systems, Yahoo DSP",
          format: 'Numeric'
     },

     // ISO-Based
     {
          value: "iso8601UTC",
          label: "ISO 8601 UTC",
          section: "ISO-Based",
          example: "2025-07-31T12:34:56Z",
          description: "ISO 8601 format with 'T' separator and 'Z' for UTC, widely used in APIs for precise timestamps. This will always convert into and return the timestamp using UTC timezone.",
          commonUses: "Google Ads, Salesforce, StackAdapt, The Trade Desk, Amazon DSP, Adform, Criteo, Choozle, DataXu, LoopMe, Bucksense, LinkedIn, Pinterest, TikTok, HubSpot, Marketo",
          format: 'YYYY-MM-DDTHH:mm:ssZ'
     },
     {
          value: "iso8601Offset",
          label: "ISO 8601 Offset",
          section: "ISO-Based",
          example: "2025-07-31T12:34:56+00:00",
          description: "ISO 8601 with 'T' separator and explicit timezone offset, used in APIs requiring timezone clarity. This will maintain the timestamps timezone and represent that timezone with a UTC offset.",
          commonUses: "Google Ads, Salesforce, The Trade Desk, Adform, DataXu, Bucksense, LinkedIn, Marketo",
          format: 'YYYY-MM-DDTHH:mm:ss+/-HH:mm'
     },
     {
          value: "iso8601Milliseconds",
          label: "ISO 8601 Milliseconds",
          section: "ISO-Based",
          example: "2025-07-31T12:34:56.000+00:00",
          description: "ISO 8601 with milliseconds and offset, for high-precision timestamps in data imports.",
          commonUses: "Salesforce",
          format: 'YYYY-MM-DDTHH:mm:ss.sss+/-HH:mm'
     },
];

export const OBJECT_PROPERTY_METRIC = {
     [SEVER_SIDE_OBJECT.PEOPLE]: {
          properties: [
               // Person Data
               ...[
                    { value: "personId", label: "PersonID" },
                    { value: "name", label: "Full Name" },
                    { value: "firstName", label: "First Name" },
                    { value: "lastName", label: "Last Name" },
                    { value: "primaryEmail", label: "Primary Email" },
                    { value: 'domain', label: 'Email Domain' },
                    { value: "emailList", label: "All Email Addresses" },
                    { value: "phone", label: "Primary Phone" },
                    { value: "phoneList", label: "Phone List" },
                    { value: 'address', label: 'Address' },
                    { value: "externalId", label: "External ID" },
                    { value: "dataSources", label: "Data Sources" },
                    { value: "revealedZipCode", label: "Revealed Zip Code" },
                    { value: "revealedCity", label: "Revealed City" },
                    { value: "revealedState", label: "Revealed State" },
                    { value: "emailType", label: "Email Type" },
                    { value: "linkedinURL", label: "LinkedIn URL" },
                    { value: "enrichTypeValue", label: "Enrichment" },
                    { value: 'linkedinSummary', label: 'Linkedin Summary' },
                    { value: 'skills', label: 'Skills'},
                    { value: 'volunteering', label: 'Volunteering' },
                    { value: 'certifications', label: 'Certification' },
                    { value: 'education', label: 'Education' },
                    { value: 'projects', label: 'Projects' },
                    { value: 'recommendations', label: 'Recommendations'},
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PERSONAL_DATA })),

               ...[{ value: 'userId', label: 'User ID' }].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.IDENTIFIER_RELATIONSHIPS })),

               // Company
               ...[
                    { value: "currentTitle", label: "Current Title" },
                    { value: "currentCompany", label: "Current Company" },
                    { value: "relatedCompanies", label: "Related Companies" },
                    { value: "otherRoles", label: "Other Roles" },
                    { value: "currentCompanyPrimaryIndustry", label: "Current Company Primary Industry" },
                    { value: "currentCompanyRevenue", label: "Current Company Revenue" },
                    { value: 'currentCompanyDomain', label: 'Current Company Domain' },
                    { value: 'employees', label: 'Current Company Total Employees' },
                    { value: 'industries', label: 'Current Company Products & Services Tags' },
                    { value: 'currentCompanyDescription', label: 'Current Company Description' },
                    { value: 'currentCompanyDescriptionShort', label: 'Current Company Short Description' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.COMPANIES })),

               ...[
                    { value: 'channelFirst', label: 'Channel - First' },
                    { value: 'channelLast', label: 'Channel - Last' },
                    { value: 'channelPath', label: 'Channel - Path' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),

               // User Source
               ...[
                    { value: "userSourceFirst", label: "User Source - First (person)" },
                    { value: "userSourcePath", label: "User Source - Path (person)" },
                    { value: "noneDirectSourcePath", label: "Non-Direct User Source - Path (person)" },
                    { value: "userSourceLast", label: "User Source - Last (person)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.USER_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),

               // Person Created Source Info
               ...[
                    { value: "userSourceFirstPersonCreated", label: "User Source - First (person created)" },
                    { value: "userSourcePathPersonCreated", label: "User Source - Path (person created)" },
                    { value: "userSourceLastPersonCreated", label: "User Source - Last (person created)" },
                    { value: 'identifiedSourceFirstND', label: 'Non-Direct User Source - First (person created)' },
                    { value: 'identifiedSourcePathND', label: 'Non-Direct User Source - Path (person created)' },
                    { value: 'identifiedSourceLastND', label: 'Non-Direct User Source - Last (person created)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO })),

               ...[
                    { value: "landingPageOriginPath", label: "Landing Page Origin Path" },
                    { value: "exitPageOriginPath", label: "Exit Page Origin Path" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO })),

               ...[
                    { value: "referrerHostname", label: "Referrer Hostname" },
                    { value: "referrerOriginPath", label: "Referrer Origin Path" },
                    { value: "referrerPath", label: "Referrer Path" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.REFERRER, sectionParent: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO })),

               ...[
                    { value: "utmSource", label: "utm_source" },
                    { value: "utmMedium", label: "utm_medium" },
                    { value: "utmCampaign", label: "utm_campaign" },
                    { value: "utmTerm", label: "utm_term" },
                    { value: "utmContent", label: "utm_content" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.UTM, sectionParent: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO })),

               ...[
                    { value: 'identifiedSourcesGclid', label: 'Click IDs (gclid)' },
                    { value: 'identifiedSourcesFbclid', label: 'Click IDs (fbclid)' },
                    { value: 'identifiedSourcesFbp', label: 'Click IDs (_fbp)' },
                    { value: 'identifiedSourcesFbc', label: 'Click IDs (_fbc)' },
                    { value: 'identifiedSourcesMsclkid', label: 'Click IDs (msclkid)' },
                    { value: 'identifiedSourcesLi_fat_id', label: 'Click IDs (li_fat_id)' },
                    { value: 'identifiedSourcesTtclid', label: 'Click IDs (ttclid)' },
                    { value: 'identifiedSourcesDclid', label: 'Click IDs (dclid)' },
                    { value: 'identifiedSourcesSrsltid', label: 'Click IDs (srsltid)' },
                    { value: 'identifiedSourcesGa', label: 'Click IDs (_ga)' },
                    { value: 'identifiedSourcesCcalltrk_user', label: 'Click IDs (calltrk_user)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CLICK_IDS, sectionParent: SECTION_OPTION_SEGMENT.PERSON_CREATED_SOURCE_INFO })),

               // Date
               ...[
                    { value: "creationDateUTC", label: "Creation Date", type: "timestamp" },
                    { value: "lastConversionDate", label: "Last Conversion Date", type: "date" },
                    { value: "firstActiveAtUTC", label: "First Active Date", type: "timestamp" },
                    { value: "lastEventDateUTC", label: "Last Event Date", type: "timestamp" },
                    { value: 'revealedAt', label: 'Revealed Date', type: "timestamp" },
                    { value: 'providedAt', label: 'First Provided Date', type: "timestamp" },
                    { value: 'importedAt', label: 'Imported Date', type: "timestamp" },
                    { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
                    { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.TIMESTAMPS })),

               // Target Account
               ...[
                    { value: "isTargetAccount", label: "Is Target Account" },
                    { value: "targetAccountDomain", label: "Target Account Domain" },
                    { value: "targetAccountGroups", label: "Target Account Groups" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.TARGET_ACCOUNT })),
          ],
          metrics: [
               ...[
                    { value: 'eventCount', label: 'Events' },
                    { value: 'sessionCount', label: 'Sessions' },
                    { value: 'avgSessionDuration', label: 'Avg Session Duration' },
                    { value: 'engagedSessionCount', label: 'Engaged Sessions' },
                    { value: 'engagementRate', label: 'Engagement Rate' },
                    { value: 'engagementScore', label: 'Lifetime Engagement Score' },
                    { value: 'bouncedSessionCount', label: 'Bounced Sessions' },
                    { value: 'bounceRate', label: 'Bounce Rate' },
                    { value: 'pageViewCount', label: 'Pageviews' },
                    { value: 'avgPageViewsPerSession', label: 'Avg Pageviews / Session' },
                    { value: 'avgVisibleTimePerPage', label: 'Avg Visible Time / Page' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY })),

               ...[
                    { value: 'sourcePathCount', label: 'Source Path Count' },
                    { value: 'noneDirectSourcePathCount', label: 'Non-Direct Source Path Count' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_DETAILS })),

               ...[
                    { value: 'conversionCount', label: 'All Conversions' },
                    { value: 'primaryConversionCount', label: 'Primary Conversions' },
                    { value: 'secondaryConversionCount', label: 'Secondary Conversions' },
                    { value: 'conversionRateAll', label: 'Conv Rate (All Conversions)' },
                    { value: 'conversionRatePrimary', label: 'Conv Rate (Primary Conversions)' },
                    { value: 'conversionRateSecondary', label: 'Conv Rate (Secondary Conversions)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),
          ]
     },
     [SEVER_SIDE_OBJECT.COMPANY]: {
          properties: [
               // Company Information
               ...[
                    { value: "companyId", label: "CompanyID" },
                    { value: 'companyName', label: 'Company Name' },
                    { value: 'description', label: 'Description' },
                    { value: 'domain', label: 'Domain' },
                    { value: "businessType", label: "Business Type" },
                    { value: "dataSources", label: "Data Sources" }
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.GENERAL_INFORMATION })),

               ...[
                    { value: 'revenue', label: 'Revenue' },
                    { value: 'employees', label: 'Total Employees' },
                    { value: "monthlyVisitors", label: "Monthly Visitor" },
                    { value: "yearFounded", label: "Year Founded" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SIZE })),

               ...[
                    { value: 'primaryIndustry', label: 'Primary Industry' },
                    { value: 'industries', label: 'Products & Services Tags' },
                    { value: 'postalcode', label: 'Postal Code' },
                    { value: 'naicsCode', label: 'Naics Code' },
                    { value: 'naicsSector', label: 'Naics Sector' },
                    { value: 'naicsSubsector', label: 'Naics Subsector' },
                    { value: 'naicsIndustryGroup', label: 'Naics IndustryGroup' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.INDUSTRY })),

               ...[
                    { value: 'companyCountry', label: 'Country' },
                    { value: 'companyState', label: 'State' },
                    { value: "city", label: "City" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LOCATION })),

               // Target Account
               ...[
                    { value: "isTargetAccount", label: "Is Target Account" },
                    { value: "targetAccountGroups", label: "Target Account Groups" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.TARGET_ACCOUNT })),

               // User Source
               ...[
                    { value: "userSourceFirst", label: "User Source - First (company)" },
                    { value: "userSourcePath", label: "User Source - Path (company)" },
                    { value: "noneDirectSourcePath", label: "Non-Direct User Source - Path (company)" },
                    { value: "userSourceLast", label: "User Source - Last (company)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_DETAILS })),

               ...[
                    { value: 'channelFirst', label: 'Channel - First' },
                    { value: 'channelLast', label: 'Channel - Last' },
                    { value: 'channelPath', label: 'Channel - Path' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),

               ...[
                    { value: "createdSourceFirst", label: "User Source - First (company created)" },
                    { value: "createdSourcePath", label: "User Source - Path (company created)" },
                    { value: "revealedSource", label: "User Source - Last (company created)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE })),

               ...[
                    { value: "landingPageOriginPath", label: "Landing Page Origin Path" },
                    { value: "exitPageOriginPath", label: "Exit Page Origin Path" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE })),

               ...[
                    { value: "referrerHostname", label: "Referrer Hostname" },
                    { value: "referrerOriginPath", label: "Referrer Origin Path" },
                    { value: "referrerPath", label: "Referrer Path" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.REFERRER, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE })),

               ...[
                    { value: "utmSource", label: "utm_source" },
                    { value: "utmMedium", label: "utm_medium" },
                    { value: "utmCampaign", label: "utm_campaign" },
                    { value: "utmTerm", label: "utm_term" },
                    { value: "utmContent", label: "utm_content" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.UTM, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE })),

               ...[
                    { value: 'createdSourcesGclid', label: 'Click IDs (gclid)' },
                    { value: 'createdSourcesFbclid', label: 'Click IDs (fbclid)' },
                    { value: 'createdSourcesFbp', label: 'Click IDs (_fbp)' },
                    { value: 'createdSourcesFbc', label: 'Click IDs (_fbc)' },
                    { value: 'createdSourcesMsclkid', label: 'Click IDs (msclkid)' },
                    { value: 'createdSourcesLi_fat_id', label: 'Click IDs (li_fat_id)' },
                    { value: 'createdSourcesTtclid', label: 'Click IDs (ttclid)' },
                    { value: 'createdSourcesDclid', label: 'Click IDs (dclid)' },
                    { value: 'createdSourcesSrsltid', label: 'Click IDs (srsltid)' },
                    { value: 'createdSourcesGa', label: 'Click IDs (_ga)' },
                    { value: 'createdSourcesCcalltrk_user', label: 'Click IDs (calltrk_user)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CLICK_IDS, sectionParent: SECTION_OPTION_SEGMENT.REVEALED_SOURCE })),

               ...[
                    { value: "createdDateUTC", label: "Creation date", type: "timestamp" },
                    { value: "latestUpdateDateUTC", label: "Latest update date", type: "timestamp" },
                    { value: "lastConversionDate", label: "Last Conversion Date", type: "date" },
                    { value: 'dataSourcesRevealedAt', label: 'Revealed Date', type: "timestamp" },
                    { value: 'dataSourcesProvidedAt', label: 'First Provided Date', type: "timestamp" },
                    { value: 'dataSourcesImportedAt', label: 'Imported Date', type: "timestamp" },
                    { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
                    { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.TIMESTAMPS })),
          ],
          metrics: [
               ...[
                    { value: "peopleCount", label: "People" },
                    { value: "userCount", label: "Users" },
                    { value: "sessionCount", label: "Sessions" },
                    { value: "engagedSessionCount", label: "Engaged Sessions" },
                    { value: 'engagementScore', label: 'Lifetime Engagement Score' },
                    { value: 'avgSessionDuration', label: 'Avg Session Duration' },
                    { value: 'avgVisibleTimePerPage', label: 'Avg Visible Time / Page' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT_ACTIVITY })),

               ...[
                    { value: 'sourcePathCount', label: 'Source Path Count' },
                    { value: 'noneDirectSourcePathCount', label: 'Non-Direct Source Path Count' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_DETAILS })),

               ...[
                    { value: 'conversionCount', label: 'All Conversions' },
                    { value: 'primaryConversionCount', label: 'Primary Conversions' },
                    { value: 'secondaryConversionCount', label: 'Secondary Conversions' },
                    { value: 'conversionRateAll', label: 'Conv Rate (All Conversions)' },
                    { value: 'conversionRatePrimary', label: 'Conv Rate (Primary Conversions)' },
                    { value: 'conversionRateSecondary', label: 'Conv Rate (Secondary Conversions)' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),
          ]
     },
     [SEVER_SIDE_OBJECT.LEAD]: {
          properties: [
               ...[
                    { value: "leadID", label: "Lead ID" },
                    { value: "crmLeadID", label: "CRM Lead ID" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SYSTEM_FIELDS })),
               ...[
                    { value: "company", label: "Company" },
                    { value: "firstName", label: "First Name" },
                    { value: "lastName", label: "Last Name" },
                    { value: "externalID", label: "ExternalID" },
                    { value: "email", label: "Email" },
                    { value: "secondaryEmail", label: "Secondary Email" },
                    { value: "personalPhone", label: "Personal Phone" },
                    { value: "secondaryPhone", label: "Secondary Phone" },
                    { value: "city", label: "City" },
                    { value: "country", label: "Country" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),
               ...[
                    { value: "createdDate", label: "Created Date", type: "timestamp" },
                    { value: "lastModifiedAt", label: "Last Modified", type: "timestamp" },
                    { value: "firstSessionAt", label: "First Session Date", type: "timestamp" },
                    { value: "lastSessionAt", label: "Last Session Date", type: "timestamp" },
                    { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
                    { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),
               ...[
                    { value: "status", label: "Status" },
                    { value: "isConverted", label: "isConverted" },
                    { value: "isQualified", label: "isQualified" },
                    { value: "rating", label: "Rating" },
                    { value: "disposition", label: "Disposition" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "emailOptOut", label: "Email Opt Out" },
                    { value: "advertisingOptOut", label: "Advertising Opt Out" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.TARGETING_FIELDS })),
               ...[
                    { value: "createdSourceFirst", label: "Created Source (first)" },
                    { value: "createdSourceLast", label: "Created Source (last)" },
                    { value: "createdSourcePath", label: "Created Source (path)" },
                    { value: "createdSourcePathND", label: "Created Source (path non-direct)" },
                    { value: "createdUtmSourcePath", label: "Created utm_source (path)" },
                    { value: "createdUtmMediumPath", label: "Created utm_medium (path)" },
                    { value: "createdUtmSourceMediumPath", label: "Created utm_source / utm_medium (path)" },
                    { value: "createdUtmCampaignPath", label: "Created utm_campaign (path)" },
                    { value: "createdUtmContentPath", label: "Created utm_content (path)" },
                    { value: "createdUtmTermPath", label: "Created utm_term (path)" },
                    { value: "createdLandingPagePath", label: "Created Landing Page (path)" },
                    { value: "createdReferrerOriginPath", label: "Created Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE, sectionParent: SECTION_REPORT.SOURCES_AND_JOURNEY })),
               ...[
                    { value: "createdChannelFirst", label: "Created Channel (first)" },
                    { value: "createdChannelLast", label: "Created Channel (last)" },
                    { value: "createdChannelPath", label: "Created Channel (path)" },
                    { value: "createdChannelPathND", label: "Created Channel (path non-direct)" },
               ].map((item) => ({ ...item, section: SECTION_REPORT.CREATED_BY, sectionParent: SECTION_REPORT.CHANNELS_AND_JOURNEY })),
               ...[
                    { value: "lifeTimeSourcePath", label: "Lifetime Source (path)" },
                    { value: "lifeTimeSourcePathND", label: "Lifetime Source (path non-direct)" },
                    { value: "lifeTimeLandingPagePath", label: "Lifetime Landing Page (path)" },
                    { value: "lifeTimeReferrerOriginPath", label: "Lifetime Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY, sectionParent: SECTION_REPORT.SOURCES_AND_JOURNEY })),
               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,
               ...CRM_IMPORTED_CLICK_ID_PROPERTY_OPTIONS,
          ],
          metrics: [
               ...[
                    { value: "lifeTimeSessionCount", label: "Sessions (lifetime)" },
                    { value: "createdSessionCount", label: "Sessions (created by)" },
                    { value: "lifeTimeUserCount", label: "Users (lifetime)" },
                    { value: "lifeTimePeopleCount", label: "People (lifetime)" },
                    { value: "lifeTimeCompany", label: "Companies (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LISTENLAYER_OBJECTS })),
               ...[
                    { value: "engagedScore", label: "Sales Engaged Score" },
                    { value: "fitScore", label: "Sales Fit Score" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "lifeTimeContactsCount", label: "Contacts (lifetime)" },
                    { value: "lifeTimeAccountsCount", label: "Accounts (lifetime)" },
                    { value: "lifeTimeOpportunitiesCount", label: "Opportunities (lifetime)" },
                    { value: "lifeTimeCampaignsCount", label: "Campaigns (lifetime)" },
                    { value: "lifeTimeEventsCount", label: "Events (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),
               ...[
                    { value: "lifeTimeEngagedSessionCount", label: "Engaged Sessions (lifetime)" },
                    { value: "lifeTimeEngagementScore", label: "Engagement Score (lifetime)" },
                    { value: "lifeTimeAvgSessionDuration", label: "Avg. Session Duration (lifetime)" },
                    { value: "lifeTimePageViewCount", label: "Pageviews (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT })),
               ...[
                    { value: "lifeTimeConversionCount", label: "All Web Conversions (lifetime)" },
                    { value: "lifeTimePrimaryConversionCount", label: "Primary Web Conversions (lifetime)" },
                    { value: "lifeTimeSecondaryConversionCount", label: "Secondary Web Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),
               ...[
                    { value: "lifeTimeSaleConversionCount", label: "All Sales Conversions (lifetime)" },
                    { value: "lifeTimeSalePrimaryConversionCount", label: "Primary Sales Conversions (lifetime)" },
                    { value: "lifeTimeSaleSecondaryConversionCount", label: "Secondary Sales Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SALES_CONVERSIONS })),
          ]
     },
     [SEVER_SIDE_OBJECT.CONTACT]: {
          properties: [
               ...[
                    { value: "contactID", label: "Contact ID" },
                    { value: "crmContactID", label: "CRM Contact ID" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SYSTEM_FIELDS })),
               ...[
                    { value: "firstName", label: "First Name" },
                    { value: "lastName", label: "Last Name" },
                    { value: "externalID", label: "ExternalID" },
                    { value: "email", label: "Email" },
                    { value: "secondaryEmail", label: "Secondary Email" },
                    { value: "personalPhone", label: "Personal Phone" },
                    { value: "secondaryPhone", label: "Secondary Phone" },
                    { value: "city", label: "City" },
                    { value: "country", label: "Country" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),
               ...[
                    { value: "createdDate", label: "Created Date", type: "timestamp" },
                    { value: "lastModifiedAt", label: "Last Modified", type: "timestamp" },
                    { value: "firstSessionAt", label: "First Session Date", type: "timestamp" },
                    { value: "lastSessionAt", label: "Last Session Date", type: "timestamp" },
                    { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
                    { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),
               ...[
                    { value: "status", label: "Status" },
                    { value: "isQualified", label: "isQualified" },
                    { value: "isCustomer", label: "isCustomer" },
                    { value: "isCustomerDate", label: "isCustomerDate", type: "date" },
                    { value: "isLostCustomer", label: "isLostCustomer" },
                    { value: "isLostCustomerDate", label: "isLostCustomerDate", type: "date" },
                    { value: "disposition", label: "Disposition" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "doNotCall", label: "Do Not Call" },
                    { value: "emailOptOut", label: "Email Opt Out" },
                    { value: "advertisingOptOut", label: "Advertising Opt Out" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.TARGETING_FIELDS })),
               ...[
                    { value: "createdSourceFirst", label: "Created Source (first)" },
                    { value: "createdSourceLast", label: "Created Source (last)" },
                    { value: "createdSourcePath", label: "Created Source (path)" },
                    { value: "createdSourcePathND", label: "Created Source (path non-direct)" },
                    { value: "createdUtmSourcePath", label: "Created utm_source (path)" },
                    { value: "createdUtmMediumPath", label: "Created utm_medium (path)" },
                    { value: "createdUtmSourceMediumPath", label: "Created utm_source / utm_medium (path)" },
                    { value: "createdUtmCampaignPath", label: "Created utm_campaign (path)" },
                    { value: "createdUtmContentPath", label: "Created utm_content (path)" },
                    { value: "createdUtmTermPath", label: "Created utm_term (path)" },
                    { value: "createdLandingPagePath", label: "Created Landing Page (path)" },
                    { value: "createdReferrerOriginPath", label: "Created Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE })),
               ...[
                    { value: "createdChannelFirst", label: "Created Channel (first)" },
                    { value: "createdChannelLast", label: "Created Channel (last)" },
                    { value: "createdChannelPath", label: "Created Channel (path)" },
                    { value: "createdChannelPathND", label: "Created Channel (path non-direct)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),
               ...[
                    { value: "lifeTimeSourcePath", label: "Lifetime Source (path)" },
                    { value: "lifeTimeSourcePathND", label: "Lifetime Source (path non-direct)" },
                    { value: "lifeTimeLandingPagePath", label: "Lifetime Landing Page (path)" },
                    { value: "lifeTimeReferrerOriginPath", label: "Lifetime Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),
               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,
               ...CRM_IMPORTED_CLICK_ID_PROPERTY_OPTIONS,
          ],
          metrics: [
               ...[
                    { value: "lifeTimeSessionCount", label: "Sessions (lifetime)" },
                    { value: "createdSessionCount", label: "Sessions (created by)" },
                    { value: "lifeTimeUserCount", label: "Users (lifetime)" },
                    { value: "lifeTimePeopleCount", label: "People (lifetime)" },
                    { value: "lifeTimeCompany", label: "Companies (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LISTENLAYER_OBJECTS })),
               ...[
                    { value: "engagedScore", label: "Sales Engaged Score" },
                    { value: "fitScore", label: "Sales Fit Score" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "lifeTimeLeadsCount", label: "Leads (lifetime)" },
                    { value: "lifeTimeAccountsCount", label: "Accounts (lifetime)" },
                    { value: "lifeTimeOpportunitiesCount", label: "Opportunities (lifetime)" },
                    { value: "lifeTimeQuotesCount", label: "Quotes (lifetime)" },
                    { value: "lifeTimeOrdersCount", label: "Orders (lifetime)" },
                    { value: "lifeTimeContractsCount", label: "Contracts (lifetime)" },
                    { value: "lifeTimeCampaignsCount", label: "Campaigns (lifetime)" },
                    { value: "lifeTimeEventsCount", label: "Events (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),
               ...[
                    { value: "lifeTimeEngagedSessionCount", label: "Engaged Sessions (lifetime)" },
                    { value: "lifeTimeEngagementScore", label: "Engagement Score (lifetime)" },
                    { value: "lifeTimeAvgSessionDuration", label: "Avg. Session Duration (lifetime)" },
                    { value: "lifeTimePageViewCount", label: "Pageviews (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT })),
               ...[
                    { value: "lifeTimeConversionCount", label: "All Web Conversions (lifetime)" },
                    { value: "lifeTimePrimaryConversionCount", label: "Primary Web Conversions (lifetime)" },
                    { value: "lifeTimeSecondaryConversionCount", label: "Secondary Web Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),
               ...[
                    { value: "lifeTimeSaleConversionCount", label: "All Sales Conversions (lifetime)" },
                    { value: "lifeTimeSalePrimaryConversionCount", label: "Primary Sales Conversions (lifetime)" },
                    { value: "lifeTimeSaleSecondaryConversionCount", label: "Secondary Sales Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SALES_CONVERSIONS })),
          ]
     },
     [SEVER_SIDE_OBJECT.PARENT_ACCOUNT]: {
          properties: [
               ...[
                    { value: "accountID", label: "Account ID" },
                    { value: "crmAccountID", label: "CRM Account ID" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SYSTEM_FIELDS })),
               ...[
                    { value: "accountName", label: "Account Name" },
                    { value: "accountType", label: "Account Type" },
                    { value: "accountIndustry", label: "Industry" },
                    { value: "accountOwnershipType", label: "Ownership Type" },
                    { value: "accountPhone", label: "Phone" },
                    { value: "domain", label: "Domain" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),
               ...[
                    { value: "createdDate", label: "Created Date", type: "timestamp" },
                    { value: "lastModifiedAt", label: "Last Modified", type: "timestamp" },
                    { value: "firstSessionAt", label: "First Session Date", type: "timestamp" },
                    { value: "lastSessionAt", label: "Last Session Date", type: "timestamp" },
                    { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
                    { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),
               ...[
                    { value: "status", label: "Status" },
                    { value: "isPartner", label: "isPartner" },
                    { value: "isQualified", label: "isQualified" },
                    { value: "rating", label: "Rating" },
                    { value: "isCustomer", label: "isCustomer" },
                    { value: "isCustomerDate", label: "isCustomerDate", type: "date" },
                    { value: "isLostCustomer", label: "isLostCustomer" },
                    { value: "isLostCustomerDate", label: "isLostCustomerDate", type: "date" },
                    { value: "disposition", label: "Disposition" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "createdSourceFirst", label: "Created Source (first)" },
                    { value: "createdSourceLast", label: "Created Source (last)" },
                    { value: "createdSourcePath", label: "Created Source (path)" },
                    { value: "createdSourcePathND", label: "Created Source (path non-direct)" },
                    { value: "createdUtmSourcePath", label: "Created utm_source (path)" },
                    { value: "createdUtmMediumPath", label: "Created utm_medium (path)" },
                    { value: "createdUtmSourceMediumPath", label: "Created utm_source / utm_medium (path)" },
                    { value: "createdUtmCampaignPath", label: "Created utm_campaign (path)" },
                    { value: "createdUtmContentPath", label: "Created utm_content (path)" },
                    { value: "createdUtmTermPath", label: "Created utm_term (path)" },
                    { value: "createdLandingPagePath", label: "Created Landing Page (path)" },
                    { value: "createdReferrerOriginPath", label: "Created Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE })),
               ...[
                    { value: "createdChannelFirst", label: "Created Channel (first)" },
                    { value: "createdChannelLast", label: "Created Channel (last)" },
                    { value: "createdChannelPath", label: "Created Channel (path)" },
                    { value: "createdChannelPathND", label: "Created Channel (path non-direct)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),
               ...[
                    { value: "lifeTimeSourcePath", label: "Lifetime Source (path)" },
                    { value: "lifeTimeSourcePathND", label: "Lifetime Source (path non-direct)" },
                    { value: "lifeTimeLandingPagePath", label: "Lifetime Landing Page (path)" },
                    { value: "lifeTimeReferrerOriginPath", label: "Lifetime Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),

               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,
          ],
          metrics: [
               ...[
                    { value: "lifeTimeSessionCount", label: "Sessions (lifetime)" },
                    { value: "createdSessionCount", label: "Sessions (created by)" },
                    { value: "lifeTimeUserCount", label: "Users (lifetime)" },
                    { value: "lifeTimePeopleCount", label: "People (lifetime)" },
                    { value: "lifeTimeCompany", label: "Companies (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LISTENLAYER_OBJECTS })),
               ...[
                    { value: "accountAnnualRevenue", label: "Annual Revenue" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.VALUES })),
               ...[
                    { value: "engagedScore", label: "Sales Engaged Score" },
                    { value: "fitScore", label: "Sales Fit Score" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "lifeTimeLeadsCount", label: "Leads (lifetime)" },
                    { value: "lifeTimeAccountsParentCount", label: "Parent Accounts (lifetime)" },
                    { value: "lifeTimeContactsCount", label: "Contacts (lifetime)" },
                    { value: "lifeTimeOpportunitiesCount", label: "Opportunities (lifetime)" },
                    { value: "lifeTimeQuotesCount", label: "Quotes (lifetime)" },
                    { value: "lifeTimeOrdersCount", label: "Orders (lifetime)" },
                    { value: "lifeTimeContractsCount", label: "Contracts (lifetime)" },
                    { value: "lifeTimeEventsCount", label: "Events (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),
               ...[
                    { value: "lifeTimeEngagedSessionCount", label: "Engaged Sessions (lifetime)" },
                    { value: "lifeTimeEngagementScore", label: "Engagement Score (lifetime)" },
                    { value: "lifeTimeAvgSessionDuration", label: "Avg. Session Duration (lifetime)" },
                    { value: "lifeTimePageViewCount", label: "Pageviews (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT })),
               ...[
                    { value: "lifeTimeConversionCount", label: "All Web Conversions (lifetime)" },
                    { value: "lifeTimePrimaryConversionCount", label: "Primary Web Conversions (lifetime)" },
                    { value: "lifeTimeSecondaryConversionCount", label: "Secondary Web Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),
               ...[
                    { value: "lifeTimeSaleConversionCount", label: "All Sales Conversions (lifetime)" },
                    { value: "lifeTimeSalePrimaryConversionCount", label: "Primary Sales Conversions (lifetime)" },
                    { value: "lifeTimeSaleSecondaryConversionCount", label: "Secondary Sales Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SALES_CONVERSIONS })),
          ]
     },
     [SEVER_SIDE_OBJECT.ACCOUNT]: {
          properties: [
               ...[
                    { value: "accountID", label: "Account ID" },
                    { value: "crmAccountID", label: "CRM Account ID" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SYSTEM_FIELDS })),
               ...[
                    { value: "accountName", label: "Account Name" },
                    { value: "accountType", label: "Account Type" },
                    { value: "accountIndustry", label: "Industry" },
                    { value: "accountOwnershipType", label: "Ownership Type" },
                    { value: "accountPhone", label: "Phone" },
                    { value: "domain", label: "Domain" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),
               ...[
                    { value: "createdDate", label: "Created Date", type: "timestamp" },
                    { value: "lastModifiedAt", label: "Last Modified", type: "timestamp" },
                    { value: "firstSessionAt", label: "First Session Date", type: "timestamp" },
                    { value: "lastSessionAt", label: "Last Session Date", type: "timestamp" },
                    { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
                    { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),
               ...[
                    { value: "status", label: "Status" },
                    { value: "isPartner", label: "isPartner" },
                    { value: "isQualified", label: "isQualified" },
                    { value: "rating", label: "Rating" },
                    { value: "isCustomer", label: "isCustomer" },
                    { value: "isCustomerDate", label: "isCustomerDate", type: "date" },
                    { value: "isLostCustomer", label: "isLostCustomer" },
                    { value: "isLostCustomerDate", label: "isLostCustomerDate", type: "date" },
                    { value: "disposition", label: "Disposition" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "createdSourceFirst", label: "Created Source (first)" },
                    { value: "createdSourceLast", label: "Created Source (last)" },
                    { value: "createdSourcePath", label: "Created Source (path)" },
                    { value: "createdSourcePathND", label: "Created Source (path non-direct)" },
                    { value: "createdUtmSourcePath", label: "Created utm_source (path)" },
                    { value: "createdUtmMediumPath", label: "Created utm_medium (path)" },
                    { value: "createdUtmSourceMediumPath", label: "Created utm_source / utm_medium (path)" },
                    { value: "createdUtmCampaignPath", label: "Created utm_campaign (path)" },
                    { value: "createdUtmContentPath", label: "Created utm_content (path)" },
                    { value: "createdUtmTermPath", label: "Created utm_term (path)" },
                    { value: "createdLandingPagePath", label: "Created Landing Page (path)" },
                    { value: "createdReferrerOriginPath", label: "Created Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE })),
               ...[
                    { value: "createdChannelFirst", label: "Created Channel (first)" },
                    { value: "createdChannelLast", label: "Created Channel (last)" },
                    { value: "createdChannelPath", label: "Created Channel (path)" },
                    { value: "createdChannelPathND", label: "Created Channel (path non-direct)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),
               ...[
                    { value: "lifeTimeSourcePath", label: "Lifetime Source (path)" },
                    { value: "lifeTimeSourcePathND", label: "Lifetime Source (path non-direct)" },
                    { value: "lifeTimeLandingPagePath", label: "Lifetime Landing Page (path)" },
                    { value: "lifeTimeReferrerOriginPath", label: "Lifetime Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),

               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,
          ],
          metrics: [
               ...[
                    { value: "lifeTimeSessionCount", label: "Sessions (lifetime)" },
                    { value: "createdSessionCount", label: "Sessions (created by)" },
                    { value: "lifeTimeUserCount", label: "Users (lifetime)" },
                    { value: "lifeTimePeopleCount", label: "People (lifetime)" },
                    { value: "lifeTimeCompany", label: "Companies (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LISTENLAYER_OBJECTS })),
               ...[
                    { value: "accountAnnualRevenue", label: "Annual Revenue" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.VALUES })),
               ...[
                    { value: "engagedScore", label: "Sales Engaged Score" },
                    { value: "fitScore", label: "Sales Fit Score" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "lifeTimeLeadsCount", label: "Leads (lifetime)" },
                    { value: "lifeTimeAccountsParentCount", label: "Parent Accounts (lifetime)" },
                    { value: "lifeTimeContactsCount", label: "Contacts (lifetime)" },
                    { value: "lifeTimeOpportunitiesCount", label: "Opportunities (lifetime)" },
                    { value: "lifeTimeQuotesCount", label: "Quotes (lifetime)" },
                    { value: "lifeTimeOrdersCount", label: "Orders (lifetime)" },
                    { value: "lifeTimeContractsCount", label: "Contracts (lifetime)" },
                    { value: "lifeTimeEventsCount", label: "Events (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),
               ...[
                    { value: "lifeTimeEngagedSessionCount", label: "Engaged Sessions (lifetime)" },
                    { value: "lifeTimeEngagementScore", label: "Engagement Score (lifetime)" },
                    { value: "lifeTimeAvgSessionDuration", label: "Avg. Session Duration (lifetime)" },
                    { value: "lifeTimePageViewCount", label: "Pageviews (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT })),
               ...[
                    { value: "lifeTimeConversionCount", label: "All Web Conversions (lifetime)" },
                    { value: "lifeTimePrimaryConversionCount", label: "Primary Web Conversions (lifetime)" },
                    { value: "lifeTimeSecondaryConversionCount", label: "Secondary Web Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),
               ...[
                    { value: "lifeTimeSaleConversionCount", label: "All Sales Conversions (lifetime)" },
                    { value: "lifeTimeSalePrimaryConversionCount", label: "Primary Sales Conversions (lifetime)" },
                    { value: "lifeTimeSaleSecondaryConversionCount", label: "Secondary Sales Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SALES_CONVERSIONS })),
          ]
     },
     [SEVER_SIDE_OBJECT.OPPORTUNITY]: {
          properties: [
               ...[
                    { value: "opportunityID", label: "Opportunity ID" },
                    { value: "crmOpportunityID", label: "CRM Opportunity ID" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SYSTEM_FIELDS })),
               ...[
                    { value: "opportunityName", label: "Opportunity Name" },
                    { value: "opportunityType", label: "Opportunity Type" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),
               ...[
                    { value: "createdDate", label: "Created Date", type: "timestamp" },
                    { value: "closedDate", label: "Closed Date", type: "timestamp" },
                    { value: "forecastedClosedDate", label: "Forecast Closed Date", type: "timestamp" },
                    { value: "lastModifiedAt", label: "Last Modified", type: "timestamp" },
                    { value: "firstSessionAt", label: "First Session Date", type: "timestamp" },
                    { value: "lastSessionAt", label: "Last Session Date", type: "timestamp" },
                    { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
                    { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),
               ...[
                    { value: "isQualified", label: "isQualified" },
                    { value: "status", label: "Status" },
                    { value: "stage", label: "Stage" },
                    { value: "isWon", label: "isWon" },
                    { value: "isClosed", label: "isClosed" },
                    { value: "isLost", label: "isLost" },
                    { value: "primarySalesStatus", label: "Primary Sales Status" },
                    { value: "disposition", label: "Disposition" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "MRRCurrency", label: "MRRCurrency" },
                    { value: "QRRCurrency", label: "QRRCurrency" },
                    { value: "ARRCurrency", label: "ARRCurrency" },
                    { value: "TCVCurrency", label: "TCVCurrency" },
                    { value: "PRJCurrency", label: "PRJCurrency" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.VALUES })),
               ...[
                    { value: "createdSourceFirst", label: "Created Source (first)" },
                    { value: "createdSourceLast", label: "Created Source (last)" },
                    { value: "createdSourcePath", label: "Created Source (path)" },
                    { value: "createdSourcePathND", label: "Created Source (path non-direct)" },
                    { value: "createdUtmSourcePath", label: "Created utm_source (path)" },
                    { value: "createdUtmMediumPath", label: "Created utm_medium (path)" },
                    { value: "createdUtmSourceMediumPath", label: "Created utm_source / utm_medium (path)" },
                    { value: "createdUtmCampaignPath", label: "Created utm_campaign (path)" },
                    { value: "createdUtmContentPath", label: "Created utm_content (path)" },
                    { value: "createdUtmTermPath", label: "Created utm_term (path)" },
                    { value: "createdLandingPagePath", label: "Created Landing Page (path)" },
                    { value: "createdReferrerOriginPath", label: "Created Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE })),
               ...[
                    { value: "createdChannelFirst", label: "Created Channel (first)" },
                    { value: "createdChannelLast", label: "Created Channel (last)" },
                    { value: "createdChannelPath", label: "Created Channel (path)" },
                    { value: "createdChannelPathND", label: "Created Channel (path non-direct)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),
               ...[
                    { value: "closedSourceLast", label: "Closed Source (last)" },
                    { value: "closedSourcePath", label: "Closed Source (path)" },
                    { value: "closedSourcePathND", label: "Closed Source (path non-direct)" },
                    { value: "closedLandingPagePath", label: "Closed Landing Page (path)" },
                    { value: "closedReferrerOriginPath", label: "Closed Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CLOSED })),
               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,
          ],
          metrics: [
               ...[
                    { value: "lifeTimeSessionCount", label: "Sessions (lifetime)" },
                    { value: "createdSessionCount", label: "Sessions (created by)" },
                    { value: "lifeTimeUserCount", label: "Users (lifetime)" },
                    { value: "lifeTimePeopleCount", label: "People (lifetime)" },
                    { value: "lifeTimeCompany", label: "Companies (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LISTENLAYER_OBJECTS })),
               ...[
                    { value: "engagedScore", label: "Sales Engaged Score" },
                    { value: "fitScore", label: "Sales Fit Score" },
                    { value: "ageInDays", label: "Age In Days" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "MRRAmount", label: "MRRAmount" },
                    { value: "MRRQuantity", label: "MRRQuantity" },
                    { value: "MRRProbability", label: "MRRProbability" },
                    { value: "QRRAmount", label: "QRRAmount" },
                    { value: "QRRQuantity", label: "QRRQuantity" },
                    { value: "QRRProbability", label: "QRRProbability" },
                    { value: "ARRAmount", label: "ARRAmount" },
                    { value: "ARRQuantity", label: "ARRQuantity" },
                    { value: "ARRProbability", label: "ARRProbability" },
                    { value: "TCVAmount", label: "TCVAmount" },
                    { value: "TCVQuantity", label: "TCVQuantity" },
                    { value: "TCVProbability", label: "TCVProbability" },
                    { value: "PRJAmount", label: "PRJAmount" },
                    { value: "PRJQuantity", label: "PRJQuantity" },
                    { value: "PRJProbability", label: "PRJProbability" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.VALUES })),
               ...[
                    { value: "lifeTimeLeadsCount", label: "Leads (lifetime)" },
                    { value: "lifeTimeContactsCount", label: "Contacts (lifetime)" },
                    { value: "lifeTimeAccountsCount", label: "Accounts (lifetime)" },
                    { value: "lifeTimeQuotesCount", label: "Quotes (lifetime)" },
                    { value: "lifeTimeOrdersCount", label: "Orders (lifetime)" },
                    { value: "lifeTimeContractsCount", label: "Contracts (lifetime)" },
                    { value: "lifeTimeCampaignsCount", label: "Campaigns (lifetime)" },
                    { value: "lifeTimeEventsCount", label: "Events (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),
               ...[
                    { value: "lifeTimeEngagedSessionCount", label: "Engaged Sessions (lifetime)" },
                    { value: "lifeTimeEngagementScore", label: "Engagement Score (lifetime)" },
                    { value: "lifeTimeAvgSessionDuration", label: "Avg. Session Duration (lifetime)" },
                    { value: "lifeTimePageViewCount", label: "Pageviews (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT })),
               ...[
                    { value: "lifeTimeConversionCount", label: "All Web Conversions (lifetime)" },
                    { value: "lifeTimePrimaryConversionCount", label: "Primary Web Conversions (lifetime)" },
                    { value: "lifeTimeSecondaryConversionCount", label: "Secondary Web Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),
               ...[
                    { value: "lifeTimeSaleConversionCount", label: "All Sales Conversions (lifetime)" },
                    { value: "lifeTimeSalePrimaryConversionCount", label: "Primary Sales Conversions (lifetime)" },
                    { value: "lifeTimeSaleSecondaryConversionCount", label: "Secondary Sales Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SALES_CONVERSIONS })),
          ]
     },
     [SEVER_SIDE_OBJECT.QUOTE]: {
          properties: [
               ...[
                    { value: "quoteID", label: "Quote ID" },
                    { value: "crmQuoteID", label: "CRM Quote ID" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SYSTEM_FIELDS })),
               ...[
                    { value: "quoteName", label: "Quote Name" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),
               ...[
                    { value: "createdDate", label: "Created Date", type: "timestamp" },
                    { value: "closedDate", label: "Closed Date", type: "timestamp" },
                    { value: "forecastedAcceptanceDate", label: "Forecast Acceptance Date", type: "timestamp" },
                    { value: "lastModifiedAt", label: "Last Modified", type: "timestamp" },
                    { value: "firstSessionAt", label: "First Session Date", type: "timestamp" },
                    { value: "lastSessionAt", label: "Last Session Date", type: "timestamp" },
                    { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
                    { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),
               ...[
                    { value: "status", label: "Status" },
                    { value: "isAccepted", label: "isAccepted" },
                    { value: "primarySalesStatus", label: "Primary Sales Status" },
                    { value: "isClosed", label: "isClosed" },
                    { value: "disposition", label: "Disposition" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "MRRCurrency", label: "MRRCurrency" },
                    { value: "QRRCurrency", label: "QRRCurrency" },
                    { value: "ARRCurrency", label: "ARRCurrency" },
                    { value: "TCVCurrency", label: "TCVCurrency" },
                    { value: "PRJCurrency", label: "PRJCurrency" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.VALUES })),
               ...[
                    { value: "createdSourceFirst", label: "Created Source (first)" },
                    { value: "createdSourceLast", label: "Created Source (last)" },
                    { value: "createdSourcePath", label: "Created Source (path)" },
                    { value: "createdSourcePathND", label: "Created Source (path non-direct)" },
                    { value: "createdUtmSourcePath", label: "Created utm_source (path)" },
                    { value: "createdUtmMediumPath", label: "Created utm_medium (path)" },
                    { value: "createdUtmSourceMediumPath", label: "Created utm_source / utm_medium (path)" },
                    { value: "createdUtmCampaignPath", label: "Created utm_campaign (path)" },
                    { value: "createdUtmContentPath", label: "Created utm_content (path)" },
                    { value: "createdUtmTermPath", label: "Created utm_term (path)" },
                    { value: "createdLandingPagePath", label: "Created Landing Page (path)" },
                    { value: "createdReferrerOriginPath", label: "Created Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE })),
               ...[
                    { value: "createdChannelFirst", label: "Created Channel (first)" },
                    { value: "createdChannelLast", label: "Created Channel (last)" },
                    { value: "createdChannelPath", label: "Created Channel (path)" },
                    { value: "createdChannelPathND", label: "Created Channel (path non-direct)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),
               ...[
                    { value: "closedSourceLast", label: "Closed Source (last)" },
                    { value: "closedSourcePath", label: "Closed Source (path)" },
                    { value: "closedSourcePathND", label: "Closed Source (path non-direct)" },
                    { value: "closedLandingPagePath", label: "Closed Landing Page (path)" },
                    { value: "closedReferrerOriginPath", label: "Closed Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CLOSED })),

               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,

          ],
          metrics: [
               ...[
                    { value: "lifeTimeSessionCount", label: "Sessions (lifetime)" },
                    { value: "createdSessionCount", label: "Sessions (created by)" },
                    { value: "lifeTimeUserCount", label: "Users (lifetime)" },
                    { value: "lifeTimePeopleCount", label: "People (lifetime)" },
                    { value: "lifeTimeCompany", label: "Companies (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LISTENLAYER_OBJECTS })),
               ...[
                    { value: "MRRAmount", label: "MRRAmount" },
                    { value: "MRRQuantity", label: "MRRQuantity" },
                    { value: "MRRProbability", label: "MRRProbability" },
                    { value: "QRRAmount", label: "QRRAmount" },
                    { value: "QRRQuantity", label: "QRRQuantity" },
                    { value: "QRRProbability", label: "QRRProbability" },
                    { value: "ARRAmount", label: "ARRAmount" },
                    { value: "ARRQuantity", label: "ARRQuantity" },
                    { value: "ARRProbability", label: "ARRProbability" },
                    { value: "TCVAmount", label: "TCVAmount" },
                    { value: "TCVQuantity", label: "TCVQuantity" },
                    { value: "TCVProbability", label: "TCVProbability" },
                    { value: "PRJAmount", label: "PRJAmount" },
                    { value: "PRJQuantity", label: "PRJQuantity" },
                    { value: "PRJProbability", label: "PRJProbability" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.VALUES })),
               ...[
                    { value: "lifeTimeAccountsCount", label: "Accounts (lifetime)" },
                    { value: "lifeTimeContactsCount", label: "Contacts (lifetime)" },
                    { value: "lifeTimeOpportunitiesCount", label: "Opportunities (lifetime)" },
                    { value: "lifeTimeOrdersCount", label: "Orders (lifetime)" },
                    { value: "lifeTimeContractsCount", label: "Contracts (lifetime)" },
                    { value: "lifeTimeEventsCount", label: "Events (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),
               ...[
                    { value: "lifeTimeEngagedSessionCount", label: "Engaged Sessions (lifetime)" },
                    { value: "lifeTimeEngagementScore", label: "Engagement Score (lifetime)" },
                    { value: "lifeTimeAvgSessionDuration", label: "Avg. Session Duration (lifetime)" },
                    { value: "lifeTimePageViewCount", label: "Pageviews (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT })),
               ...[
                    { value: "lifeTimeConversionCount", label: "All Web Conversions (lifetime)" },
                    { value: "lifeTimePrimaryConversionCount", label: "Primary Web Conversions (lifetime)" },
                    { value: "lifeTimeSecondaryConversionCount", label: "Secondary Web Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),
               ...[
                    { value: "lifeTimeSaleConversionCount", label: "All Sales Conversions (lifetime)" },
                    { value: "lifeTimeSalePrimaryConversionCount", label: "Primary Sales Conversions (lifetime)" },
                    { value: "lifeTimeSaleSecondaryConversionCount", label: "Secondary Sales Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SALES_CONVERSIONS })),
          ]
     },
     [SEVER_SIDE_OBJECT.ORDER]: {
          properties: [
               ...[
                    { value: "orderID", label: "Order ID" },
                    { value: "crmOrderID", label: "CRM Order ID" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SYSTEM_FIELDS })),
               ...[
                    { value: "orderName", label: "Order Name" },
                    { value: "orderNumber", label: "Order Number" },
                    { value: "orderType", label: "Order Type" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),
               ...[
                    { value: "createdDate", label: "Created Date", type: "timestamp" },
                    { value: "closedDate", label: "Closed Date", type: "timestamp" },
                    { value: "forecastedActivationDate", label: "Forecast Activation Date", type: "timestamp" },
                    { value: "lastModifiedAt", label: "Last Modified", type: "timestamp" },
                    { value: "firstSessionAt", label: "First Session Date", type: "timestamp" },
                    { value: "lastSessionAt", label: "Last Session Date", type: "timestamp" },
                    { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
                    { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),
               ...[
                    { value: "status", label: "Status" },
                    { value: "primarySalesStatus", label: "Primary Sales Status" },
                    { value: "isAccepted", label: "isAccepted" },
                    { value: "isClosed", label: "isClosed" },
                    { value: "disposition", label: "Disposition" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "MRRCurrency", label: "MRRCurrency" },
                    { value: "QRRCurrency", label: "QRRCurrency" },
                    { value: "ARRCurrency", label: "ARRCurrency" },
                    { value: "TCVCurrency", label: "TCVCurrency" },
                    { value: "PRJCurrency", label: "PRJCurrency" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.VALUES })),
               ...[
                    { value: "createdSourceFirst", label: "Created Source (first)" },
                    { value: "createdSourceLast", label: "Created Source (last)" },
                    { value: "createdSourcePath", label: "Created Source (path)" },
                    { value: "createdSourcePathND", label: "Created Source (path non-direct)" },
                    { value: "createdUtmSourcePath", label: "Created utm_source (path)" },
                    { value: "createdUtmMediumPath", label: "Created utm_medium (path)" },
                    { value: "createdUtmSourceMediumPath", label: "Created utm_source / utm_medium (path)" },
                    { value: "createdUtmCampaignPath", label: "Created utm_campaign (path)" },
                    { value: "createdUtmContentPath", label: "Created utm_content (path)" },
                    { value: "createdUtmTermPath", label: "Created utm_term (path)" },
                    { value: "createdLandingPagePath", label: "Created Landing Page (path)" },
                    { value: "createdReferrerOriginPath", label: "Created Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE })),
               ...[
                    { value: "createdChannelFirst", label: "Created Channel (first)" },
                    { value: "createdChannelLast", label: "Created Channel (last)" },
                    { value: "createdChannelPath", label: "Created Channel (path)" },
                    { value: "createdChannelPathND", label: "Created Channel (path non-direct)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),
               ...[
                    { value: "closedSourceLast", label: "Closed Source (last)" },
                    { value: "closedSourcePath", label: "Closed Source (path)" },
                    { value: "closedSourcePathND", label: "Closed Source (path non-direct)" },
                    { value: "closedLandingPagePath", label: "Closed Landing Page (path)" },
                    { value: "closedReferrerOriginPath", label: "Closed Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CLOSED })),

               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,
          ],
          metrics: [
               ...[
                    { value: "lifeTimeSessionCount", label: "Sessions (lifetime)" },
                    { value: "createdSessionCount", label: "Sessions (created by)" },
                    { value: "lifeTimeUserCount", label: "Users (lifetime)" },
                    { value: "lifeTimePeopleCount", label: "People (lifetime)" },
                    { value: "lifeTimeCompany", label: "Companies (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LISTENLAYER_OBJECTS })),
               ...[
                    { value: "MRRAmount", label: "MRRAmount" },
                    { value: "MRRQuantity", label: "MRRQuantity" },
                    { value: "MRRProbability", label: "MRRProbability" },
                    { value: "QRRAmount", label: "QRRAmount" },
                    { value: "QRRQuantity", label: "QRRQuantity" },
                    { value: "QRRProbability", label: "QRRProbability" },
                    { value: "ARRAmount", label: "ARRAmount" },
                    { value: "ARRQuantity", label: "ARRQuantity" },
                    { value: "ARRProbability", label: "ARRProbability" },
                    { value: "TCVAmount", label: "TCVAmount" },
                    { value: "TCVQuantity", label: "TCVQuantity" },
                    { value: "TCVProbability", label: "TCVProbability" },
                    { value: "PRJAmount", label: "PRJAmount" },
                    { value: "PRJQuantity", label: "PRJQuantity" },
                    { value: "PRJProbability", label: "PRJProbability" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.VALUES })),
               ...[
                    { value: "lifeTimeAccountsCount", label: "Accounts (lifetime)" },
                    { value: "lifeTimeContactsCount", label: "Contacts (lifetime)" },
                    { value: "lifeTimeOpportunitiesCount", label: "Opportunities (lifetime)" },
                    { value: "lifeTimeOrdersCount", label: "Orders (lifetime)" },
                    { value: "lifeTimeContractsCount", label: "Contracts (lifetime)" },
                    { value: "lifeTimeEventsCount", label: "Events (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),
               ...[
                    { value: "lifeTimeEngagedSessionCount", label: "Engaged Sessions (lifetime)" },
                    { value: "lifeTimeEngagementScore", label: "Engagement Score (lifetime)" },
                    { value: "lifeTimeAvgSessionDuration", label: "Avg. Session Duration (lifetime)" },
                    { value: "lifeTimePageViewCount", label: "Pageviews (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT })),
               ...[
                    { value: "lifeTimeConversionCount", label: "All Web Conversions (lifetime)" },
                    { value: "lifeTimePrimaryConversionCount", label: "Primary Web Conversions (lifetime)" },
                    { value: "lifeTimeSecondaryConversionCount", label: "Secondary Web Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),
               ...[
                    { value: "lifeTimeSaleConversionCount", label: "All Sales Conversions (lifetime)" },
                    { value: "lifeTimeSalePrimaryConversionCount", label: "Primary Sales Conversions (lifetime)" },
                    { value: "lifeTimeSaleSecondaryConversionCount", label: "Secondary Sales Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SALES_CONVERSIONS })),
          ]
     },
     [SEVER_SIDE_OBJECT.CONTRACT]: {
          properties: [
               ...[
                    { value: "contractID", label: "Contract ID" },
                    { value: "crmContractID", label: "CRM Contract ID" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SYSTEM_FIELDS })),
               ...[
                    { value: "contractName", label: "Contract Name" },
                    { value: "contractNumber", label: "Contract Number" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),
               ...[
                    { value: "createdDate", label: "Created Date", type: "timestamp" },
                    { value: "closedDate", label: "Closed Date", type: "timestamp" },
                    { value: "forecastedActivationDate", label: "Forecast Activation Date", type: "timestamp" },
                    { value: "lastModifiedAt", label: "Last Modified", type: "timestamp" },
                    { value: "firstSessionAt", label: "First Session Date", type: "timestamp" },
                    { value: "lastSessionAt", label: "Last Session Date", type: "timestamp" },
                    { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
                    { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),
               ...[
                    { value: "status", label: "Status" },
                    { value: "isClosed", label: "isClosed" },
                    { value: "primarySalesStatus", label: "Primary Sales Status" },
                    { value: "isAccepted", label: "isAccepted" },
                    { value: "disposition", label: "Disposition" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "MRRCurrency", label: "MRRCurrency" },
                    { value: "QRRCurrency", label: "QRRCurrency" },
                    { value: "ARRCurrency", label: "ARRCurrency" },
                    { value: "TCVCurrency", label: "TCVCurrency" },
                    { value: "PRJCurrency", label: "PRJCurrency" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.VALUES })),
               ...[
                    { value: "createdSourceFirst", label: "Created Source (first)" },
                    { value: "createdSourceLast", label: "Created Source (last)" },
                    { value: "createdSourcePath", label: "Created Source (path)" },
                    { value: "createdSourcePathND", label: "Created Source (path non-direct)" },
                    { value: "createdUtmSourcePath", label: "Created utm_source (path)" },
                    { value: "createdUtmMediumPath", label: "Created utm_medium (path)" },
                    { value: "createdUtmSourceMediumPath", label: "Created utm_source / utm_medium (path)" },
                    { value: "createdUtmCampaignPath", label: "Created utm_campaign (path)" },
                    { value: "createdUtmContentPath", label: "Created utm_content (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE })),
               ...[
                    { value: "createdChannelFirst", label: "Created Channel (first)" },
                    { value: "createdChannelLast", label: "Created Channel (last)" },
                    { value: "createdChannelPath", label: "Created Channel (path)" },
                    { value: "createdChannelPathND", label: "Created Channel (path non-direct)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),
               ...[
                    { value: "closedSourceLast", label: "Closed Source (last)" },
                    { value: "closedSourcePath", label: "Closed Source (path)" },
                    { value: "closedSourcePathND", label: "Closed Source (path non-direct)" },
                    { value: "closedLandingPagePath", label: "Closed Landing Page (path)" },
                    { value: "closedReferrerOriginPath", label: "Closed Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CLOSED })),

               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,
          ],
          metrics: [
               ...[
                    { value: "lifeTimeSessionCount", label: "Sessions (lifetime)" },
                    { value: "createdSessionCount", label: "Sessions (created by)" },
                    { value: "lifeTimeUserCount", label: "Users (lifetime)" },
                    { value: "lifeTimePeopleCount", label: "People (lifetime)" },
                    { value: "lifeTimeCompany", label: "Companies (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LISTENLAYER_OBJECTS })),
               ...[
                    { value: "MRRAmount", label: "MRRAmount" },
                    { value: "MRRQuantity", label: "MRRQuantity" },
                    { value: "MRRProbability", label: "MRRProbability" },
                    { value: "QRRAmount", label: "QRRAmount" },
                    { value: "QRRQuantity", label: "QRRQuantity" },
                    { value: "QRRProbability", label: "QRRProbability" },
                    { value: "ARRAmount", label: "ARRAmount" },
                    { value: "ARRQuantity", label: "ARRQuantity" },
                    { value: "ARRProbability", label: "ARRProbability" },
                    { value: "TCVAmount", label: "TCVAmount" },
                    { value: "TCVQuantity", label: "TCVQuantity" },
                    { value: "TCVProbability", label: "TCVProbability" },
                    { value: "PRJAmount", label: "PRJAmount" },
                    { value: "PRJQuantity", label: "PRJQuantity" },
                    { value: "PRJProbability", label: "PRJProbability" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.VALUES })),
               ...[
                    { value: "lifeTimeAccountsCount", label: "Accounts (lifetime)" },
                    { value: "lifeTimeOrdersCount", label: "Orders (lifetime)" },
                    { value: "lifeTimeQuotesCount", label: "Quotes (lifetime)" },
                    { value: "lifeTimeOpportunitiesCount", label: "Opportunities (lifetime)" },
                    { value: "lifeTimeContactsCount", label: "Contacts (lifetime)" },
                    { value: "lifeTimeEventsCount", label: "Events (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),
               ...[
                    { value: "lifeTimeEngagedSessionCount", label: "Engaged Sessions (lifetime)" },
                    { value: "lifeTimeEngagementScore", label: "Engagement Score (lifetime)" },
                    { value: "lifeTimeAvgSessionDuration", label: "Avg. Session Duration (lifetime)" },
                    { value: "lifeTimePageViewCount", label: "Pageviews (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT })),
               ...[
                    { value: "lifeTimeConversionCount", label: "All Web Conversions (lifetime)" },
                    { value: "lifeTimePrimaryConversionCount", label: "Primary Web Conversions (lifetime)" },
                    { value: "lifeTimeSecondaryConversionCount", label: "Secondary Web Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),
               ...[
                    { value: "lifeTimeSaleConversionCount", label: "All Sales Conversions (lifetime)" },
                    { value: "lifeTimeSalePrimaryConversionCount", label: "Primary Sales Conversions (lifetime)" },
                    { value: "lifeTimeSaleSecondaryConversionCount", label: "Secondary Sales Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SALES_CONVERSIONS })),
          ]
     },
     [SEVER_SIDE_OBJECT.CAMPAIGN]: {
          properties: [
               ...[
                    { value: "campaignID", label: "Campaign ID" },
                    { value: "crmCampaignID", label: "CRM Campaign ID" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SYSTEM_FIELDS })),
               ...[
                    { value: "campaignName", label: "Campaign Name" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),
               ...[
                    { value: "createdDate", label: "Created Date", type: "timestamp" },
                    { value: "lastModifiedAt", label: "Last Modified", type: "timestamp" },
                    { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
                    { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.TIMESTAMPS })),
               ...[
                    { value: "status", label: "Status" },
                    { value: "disposition", label: "Disposition" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
          ],
          metrics: [
               ...[
                    { value: "lifeTimeContactsCount", label: "Contacts (lifetime)" },
                    { value: "lifeTimeLeadsCount", label: "Leads (lifetime)" },
                    { value: "lifeTimeOpportunitiesCount", label: "Opportunities (lifetime)" },
                    { value: "lifeTimeEventsCount", label: "Events (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),
               ...[
                    { value: "lifeTimeSaleConversionCount", label: "All Sales Conversions (lifetime)" },
                    { value: "lifeTimeSalePrimaryConversionCount", label: "Primary Sales Conversions (lifetime)" },
                    { value: "lifeTimeSaleSecondaryConversionCount", label: "Secondary Sales Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SALES_CONVERSIONS })),
          ]
     },
     [SEVER_SIDE_OBJECT.EVENT]: {
          properties: [
               ...[
                    { value: "activityEventID", label: "Activity Event ID" },
                    { value: "crmActivityEventID", label: "CRM Activity Event ID" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SYSTEM_FIELDS })),
               ...[
                    { value: "activityEventSubject", label: "Activity Event Subject" },
                    { value: "activityEventStartDateTime", label: "Activity Event StartDateTime" },
                    { value: "activityEventLocation", label: "Activity Event Location" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),
               ...[
                    { value: "createdDate", label: "Created Date", type: "timestamp" },
                    { value: "lastModifiedAt", label: "Last Modified", type: "timestamp" },
                    { value: "firstSessionAt", label: "First Session Date", type: "timestamp" },
                    { value: "lastSessionAt", label: "Last Session Date", type: "timestamp" },
                    { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
                    { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),
               ...[
                    { value: "disposition", label: "Disposition" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "createdSourceFirst", label: "Created Source (first)" },
                    { value: "createdSourceLast", label: "Created Source (last)" },
                    { value: "createdSourcePath", label: "Created Source (path)" },
                    { value: "createdSourcePathND", label: "Created Source (path non-direct)" },
                    { value: "createdUtmSourcePath", label: "Created utm_source (path)" },
                    { value: "createdUtmMediumPath", label: "Created utm_medium (path)" },
                    { value: "createdUtmSourceMediumPath", label: "Created utm_source / utm_medium (path)" },
                    { value: "createdUtmCampaignPath", label: "Created utm_campaign (path)" },
                    { value: "createdUtmContentPath", label: "Created utm_content (path)" },
                    { value: "createdUtmTermPath", label: "Created utm_term (path)" },
                    { value: "createdLandingPagePath", label: "Created Landing Page (path)" },
                    { value: "createdReferrerOriginPath", label: "Created Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE })),
               ...[
                    { value: "createdChannelFirst", label: "Created Channel (first)" },
                    { value: "createdChannelLast", label: "Created Channel (last)" },
                    { value: "createdChannelPath", label: "Created Channel (path)" },
                    { value: "createdChannelPathND", label: "Created Channel (path non-direct)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),
               ...[
                    { value: "lifeTimeSourcePath", label: "Lifetime Source (path)" },
                    { value: "lifeTimeSourcePathND", label: "Lifetime Source (path non-direct)" },
                    { value: "lifeTimeLandingPagePath", label: "Lifetime Landing Page (path)" },
                    { value: "lifeTimeReferrerOriginPath", label: "Lifetime Referrer OriginPath (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),

               ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,
          ],
          metrics: [
               ...[
                    { value: "lifeTimeSessionCount", label: "Sessions (lifetime)" },
                    { value: "createdSessionCount", label: "Sessions (created by)" },
                    { value: "lifeTimeUserCount", label: "Users (lifetime)" },
                    { value: "lifeTimePeopleCount", label: "People (lifetime)" },
                    { value: "lifeTimeCompany", label: "Companies (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LISTENLAYER_OBJECTS })),
               ...[
                    { value: "lifeTimeAccountsCount", label: "Accounts (lifetime)" },
                    { value: "lifeTimeOrdersCount", label: "Orders (lifetime)" },
                    { value: "lifeTimeQuotesCount", label: "Quotes (lifetime)" },
                    { value: "lifeTimeCampaignsCount", label: "Campaigns (lifetime)" },
                    { value: "lifeTimeContractsCount", label: "Contracts (lifetime)" },
                    { value: "lifeTimeOpportunitiesCount", label: "Opportunities (lifetime)" },
                    { value: "lifeTimeContactsCount", label: "Contacts (lifetime)" },
                    { value: "lifeTimeLeadsCount", label: "Leads (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),
               ...[
                    { value: "lifeTimeEngagedSessionCount", label: "Engaged Sessions (lifetime)" },
                    { value: "lifeTimeEngagementScore", label: "Engagement Score (lifetime)" },
                    { value: "lifeTimeAvgSessionDuration", label: "Avg. Session Duration (lifetime)" },
                    { value: "lifeTimePageViewCount", label: "Pageviews (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT })),
               ...[
                    { value: "lifeTimeConversionCount", label: "All Web Conversions (lifetime)" },
                    { value: "lifeTimePrimaryConversionCount", label: "Primary Web Conversions (lifetime)" },
                    { value: "lifeTimeSecondaryConversionCount", label: "Secondary Web Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),
               ...[
                    { value: "lifeTimeSaleConversionCount", label: "All Sales Conversions (lifetime)" },
                    { value: "lifeTimeSalePrimaryConversionCount", label: "Primary Sales Conversions (lifetime)" },
                    { value: "lifeTimeSaleSecondaryConversionCount", label: "Secondary Sales Conversions (lifetime)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SALES_CONVERSIONS })),
          ]
     },
     [SEVER_SIDE_OBJECT.SALES_CONVERSION_EVENT]: {
          properties: [
               // DETAILS
               ...[
                    { value: 'eventId', label: 'Event ID' },
                    { value: 'conversionName', label: 'Conversion Name' },
                    { value: 'conversionType', label: 'Conversion Type' },
                    { value: 'primaryObject', label: 'Primary Object' },
                    { value: 'primaryName', label: 'Primary Name' },
                    { value: 'primaryID', label: 'Primary ID' },
                    { value: 'crmPrimaryID', label: 'CRM Primary ID' },
                    { value: 'secondaryObject', label: 'Secondary Object' },
                    { value: 'secondaryName', label: 'Secondary Name' },
                    { value: 'secondaryID', label: 'Secondary ID' },
                    { value: 'crmSecondaryID', label: 'CRM Secondary ID' },
               ].map((item) => ({ ...item, section: SECTION_REPORT.DETAILS })),

               // DATE
               ...[
                    { value: 'date', label: 'Date', type: 'date' },
                    { value: 'hour', label: 'Hour' },
                    { value: 'dayOfWeek', label: 'Day of Week' },
                    { value: 'isoWeek', label: 'ISO Week' },
                    { value: 'isoWeekDateStart', label: 'ISO Week Date Start', type: 'date' },
                    { value: 'isoWeekDateEnd', label: 'ISO Week Date End', type: 'date' },
                    { value: 'month', label: 'Month' },
                    { value: 'yearMonth', label: 'Year Month' },
                    { value: 'quarter', label: 'Quarter' },
                    { value: 'yearQuarter', label: 'Year Quarter' },
               ].map((item) => ({ ...item, section: SECTION_REPORT.DATE_ACCOUNT })),

               // SOURCE FIRST
               ...[
                    { value: 'userSourceFirst', label: 'Created Source (first)' },
                    { value: 'userSourceFirstND', label: 'Created Source (first non-direct)' },
                    { value: 'utmSourceFirst', label: 'utm_source (first)' },
                    { value: 'utmMediumFirst', label: 'utm_medium (first)' },
                    { value: 'utmSourceMediumFirst', label: 'utm_source / utm_medium (first)' },
                    { value: 'utmCampaignFirst', label: 'utm_campaign (first)' },
                    { value: 'utmContentFirst', label: 'utm_content (first)' },
                    { value: 'utmTermFirst', label: 'utm_term (first)' },
                    { value: 'referrerHostnameFirst', label: 'Referrer Hostname (first)' },
                    { value: 'referrerOriginPathFirst', label: 'Referrer Origin Path (first)' },
                    { value: 'landingPageOriginPathFirst', label: 'Landing Page (first)' },
               ].map((item) => ({ ...item, section: SECTION_REPORT.SOURCE_FIRST })),

               // SOURCE LAST
               ...[
                    { value: 'userSourceLast', label: 'Created Source (last)' },
                    { value: 'userSourceLastND', label: 'Created Source (last non-direct)' },
                    { value: 'utmSourceLast', label: 'utm_source (last)' },
                    { value: 'utmMediumLast', label: 'utm_medium (last)' },
                    { value: 'utmSourceMediumLast', label: 'utm_source / utm_medium (last)' },
                    { value: 'utmCampaignLast', label: 'utm_campaign (last)' },
                    { value: 'utmContentLast', label: 'utm_content (last)' },
                    { value: 'utmTermLast', label: 'utm_term (last)' },
                    { value: 'referrerHostnameLast', label: 'Referrer Hostname (last)' },
                    { value: 'referrerOriginPathLast', label: 'Referrer Origin Path (last)' },
                    { value: 'landingPageOriginPathLast', label: 'Landing Page (last)' },
               ].map((item) => ({ ...item, section: SECTION_REPORT.SOURCE_LAST })),

               // SOURCE PATH
               ...[
                    { value: 'userSourcePath', label: 'Created Source (path)' },
                    { value: 'userSourcePathND', label: 'Created Source (path non-direct)' },
                    { value: 'utmSourcePath', label: 'utm_source (path)' },
                    { value: 'utmMediumPath', label: 'utm_medium (path)' },
                    { value: 'utmSourceMediumPath', label: 'utm_source / utm_medium (path)' },
                    { value: 'utmCampaignPath', label: 'utm_campaign (path)' },
                    { value: 'utmContentPath', label: 'utm_content (path)' },
                    { value: 'utmTermPath', label: 'utm_term (path)' },
                    { value: 'referrerHostnamePath', label: 'Referrer Hostname (path)' },
                    { value: 'referrerOriginPath', label: 'Referrer Origin Path (path)' },
                    { value: 'landingPageOriginPath', label: 'Landing Page (path)' },
               ].map((item) => ({ ...item, section: SECTION_REPORT.SOURCE_PATH })),

               // CHANNEL
               ...[
                    { value: 'channelFirst', label: 'Created Channel (first)' },
                    { value: 'channelLast', label: 'Created Channel (last)' },
                    { value: 'channelPath', label: 'Created Channel (path)' },
               ].map((item) => ({ ...item, section: SECTION_REPORT.CREATED_CHANNEL })),

               // VALUES
               ...[
                    { value: 'conversionValue', label: 'Conversion Value' },
                    { value: 'conversionCurrency', label: 'Conversion Currency' },
               ].map((item) => ({ ...item, section: SECTION_REPORT.VALUES })),

               // TIMESTAMPS
               ...[
                    { value: 'dataActionTimestampUTC', label: 'Data Action Timestamp UTC', type: 'timestamp' },
                    { value: 'dataActionTimestamp', label: 'Data Action Timestamp', type: 'timestamp' },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.TIMESTAMPS })),
          ],
          metrics: [
               ...[
                    { value: 'conversionCount', label: 'Events' },
               ].map((item) => ({ ...item, section: SECTION_REPORT.COUNTS })),
          ]
     }
}

export const CUSTOM_SF_PROPERTY_METRIC = {
     properties: [
          ...[
               { value: "customObjectID", label: "CustomObject ID" },
               { value: "crmCustomObjectID", label: "CRM CustomObject ID" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SYSTEM_FIELDS })),
          ...[
               { value: "recordName", label: "Record Name" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),
          ...[
               { value: "createdDate", label: "Created Date", type: "timestamp" },
               { value: "lastModifiedAt", label: "Last Modified", type: "timestamp" },
               { value: "firstSessionAt", label: "First Session Date", type: "timestamp" },
               { value: "lastSessionAt", label: "Last Session Date", type: "timestamp" },
               { value: "closedDate", label: "Closed Date", type: "timestamp" },
               { value: "forecastedClosedDate", label: "Forecast Closed Date", type: "timestamp" },
               { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
               { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),
          ...[
               { value: "isClosed", label: "Is Closed" },
               { value: "primarySalesStatus", label: "Primary Sales Status" },
               { value: "isAccepted", label: "Is Accepted" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
          ...[
               { value: "MRRCurrency", label: "MRR Currency" },
               { value: "QRRCurrency", label: "QRR Currency" },
               { value: "ARRCurrency", label: "ARR Currency" },
               { value: "TCVCurrency", label: "TCV Currency" },
               { value: "PRJCurrency", label: "PRJ Currency" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.VALUES })),
          ...[
               { value: "createdSourceFirst", label: "Created Source (first)" },
               { value: "createdSourceLast", label: "Created Source (last)" },
               { value: "createdSourcePath", label: "Created Source (path)" },
               { value: "createdSourcePathND", label: "Created Source (path non-direct)" },
               { value: "createdUtmSourcePath", label: "Created utm_source (path)" },
               { value: "createdUtmMediumPath", label: "Created utm_medium (path)" },
               { value: "createdUtmSourceMediumPath", label: "Created utm_source / utm_medium (path)" },
               { value: "createdUtmCampaignPath", label: "Created utm_campaign (path)" },
               { value: "createdUtmContentPath", label: "Created utm_content (path)" },
               { value: "createdUtmTermPath", label: "Created utm_term (path)" },
               { value: "createdLandingPagePath", label: "Created Landing Page (path)" },
               { value: "createdReferrerOriginPath", label: "Created Referrer OriginPath (path)" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE })),
          ...[
               { value: "createdChannelFirst", label: "Created Channel (first)" },
               { value: "createdChannelLast", label: "Created Channel (last)" },
               { value: "createdChannelPath", label: "Created Channel (path)" },
               { value: "createdChannelPathND", label: "Created Channel (path non-direct)" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),
          ...[
               { value: "closedSourcePath", label: "Closed Source (path)" },
               { value: "closedSourcePathND", label: "Closed Source (path non-direct)" },
               { value: "closedLandingPagePath", label: "Closed Landing Page (path)" },
               { value: "closedReferrerOriginPath", label: "Closed Referrer OriginPath (path)" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CLOSED })),
     ],
     metrics: [
          ...[
               { value: "lifeTimeSessionCount", label: "Sessions (lifetime)" },
               { value: "createdSessionCount", label: "Sessions (created by)" },
               { value: "lifeTimeUserCount", label: "Users (lifetime)" },
               { value: "lifeTimePeopleCount", label: "People (lifetime)" },
               { value: "lifeTimeCompany", label: "Companies (lifetime)" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LISTENLAYER_OBJECTS })),
          ...[
               { value: "lifeTimeAccountsCount", label: "Accounts (lifetime)" },
               { value: "lifeTimeOrdersCount", label: "Orders (lifetime)" },
               { value: "lifeTimeQuotesCount", label: "Quotes (lifetime)" },
               { value: "lifeTimeCampaignsCount", label: "Campaigns (lifetime)" },
               { value: "lifeTimeContractsCount", label: "Contracts (lifetime)" },
               { value: "lifeTimeOpportunitiesCount", label: "Opportunities (lifetime)" },
               { value: "lifeTimeContactsCount", label: "Contacts (lifetime)" },
               { value: "lifeTimeLeadsCount", label: "Leads (lifetime)" },
               { value: "lifeTimeEventsCount", label: "Events (lifetime)" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.EXTERNAL_OBJECTS })),
          ...[
               { value: "MRRAmount", label: "MRR Amount" },
               { value: "MRRQuantity", label: "MRR Quantity" },
               { value: "MRRProbability", label: "MRR Probability" },
               { value: "QRRAmount", label: "QRR Amount" },
               { value: "QRRQuantity", label: "QRR Quantity" },
               { value: "QRRProbability", label: "QRR Probability" },
               { value: "ARRAmount", label: "ARR Amount" },
               { value: "ARRQuantity", label: "ARR Quantity" },
               { value: "ARRProbability", label: "ARR Probability" },
               { value: "TCVAmount", label: "TCV Amount" },
               { value: "TCVQuantity", label: "TCV Quantity" },
               { value: "TCVProbability", label: "TCV Probability" },
               { value: "PRJAmount", label: "PRJ Amount" },
               { value: "PRJQuantity", label: "PRJ Quantity" },
               { value: "PRJProbability", label: "PRJ Probability" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.VALUES })),
          ...[
               { value: "lifeTimeEngagedSessionCount", label: "Engaged Sessions (lifetime)" },
               { value: "lifeTimeEngagementScore", label: "Engagement Score (lifetime)" },
               { value: "lifeTimeAvgSessionDuration", label: "Avg. Session Duration (lifetime)" },
               { value: "lifeTimePageViewCount", label: "Pageviews (lifetime)" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT })),
          ...[
               { value: "lifeTimeConversionCount", label: "All Web Conversions (lifetime)" },
               { value: "lifeTimePrimaryConversionCount", label: "Primary Web Conversions (lifetime)" },
               { value: "lifeTimeSecondaryConversionCount", label: "Secondary Web Conversions (lifetime)" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),
          ...[
               { value: "lifeTimeSaleConversionCount", label: "All Sales Conversions (lifetime)" },
               { value: "lifeTimeSalePrimaryConversionCount", label: "Primary Sales Conversions (lifetime)" },
               { value: "lifeTimeSaleSecondaryConversionCount", label: "Secondary Sales Conversions (lifetime)" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SALES_CONVERSIONS })),
     ]
};

export const CUSTOM_EXTERNAL_PROPERTY_METRIC = {
     properties: [
          ...[
               { value: "recordName", label: "Record Name" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),

          ...[
               { value: "createdDate", label: "Created Date", type: "timestamp" },
               { value: "lastModifiedAt", label: "Last Modified", type: "timestamp" },
               { value: "firstSessionAt", label: "First Session Date", type: "timestamp" },
               { value: "lastSessionAt", label: "Last Session Date", type: "timestamp" },
               { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
               { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),
          
          ...[
               { value: "createdSourceFirst", label: "Created Source (first)" },
               { value: "createdSourceLast", label: "Created Source (last)" },
               { value: "createdSourcePath", label: "Created Source (path)" },
               { value: "createdSourcePathND", label: "Created Source (path non-direct)" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE })),
          ...[
               { value: "createdChannelFirst", label: "Created Channel (first)" },
               { value: "createdChannelLast", label: "Created Channel (last)" },
               { value: "createdChannelPath", label: "Created Channel (path)" },
               { value: "createdChannelPathND", label: "Created Channel (path non-direct)" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CHANNEL })),
          ...[
               { value: "createdLandingPagePath", label: "Created Landing Page (path)" },
               { value: "createdReferrerOriginPath", label: "Created Referrer Origin (path)" },
               { value: "createdUtmSourcePath", label: "Created utm_source (path)" },
               { value: "createdUtmMediumPath", label: "Created utm_medium (path)" },
               { value: "createdUtmSourceMediumPath", label: "Created utm_source / utm_medium (path)" },
               { value: "createdUtmCampaignPath", label: "Created utm_campaign (path)" },
               { value: "createdUtmContentPath", label: "Created utm_content (path)" },
               { value: "createdUtmTermPath", label: "Created utm_term (path)" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE })),

          ...CRM_CREATED_CLICK_ID_PROPERTY_OPTIONS,
     ],
     metrics: [
          ...[
               { value: "lifeTimeSessionCount", label: "Sessions (lifetime)" },
               { value: "createdSessionCount", label: "Sessions (created by)" },
               { value: "lifeTimeUserCount", label: "Users (lifetime)" },
               { value: "lifeTimePeopleCount", label: "People (lifetime)" },
               { value: "lifeTimeCompany", label: "Companies (lifetime)" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LISTENLAYER_OBJECTS })),
          ...[
               { value: "lifeTimeEngagedSessionCount", label: "Engaged Sessions (lifetime)" },
               { value: "lifeTimeEngagementScore", label: "Engagement Score (lifetime)" },
               { value: "lifeTimeAvgSessionDuration", label: "Avg. Session Duration (lifetime)" },
               { value: "lifeTimePageViewCount", label: "Pageviews (lifetime)" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.ENGAGEMENT })),
          ...[
               { value: "lifeTimeConversionCount", label: "All Web Conversions (lifetime)" },
               { value: "lifeTimePrimaryConversionCount", label: "Primary Web Conversions (lifetime)" },
               { value: "lifeTimeSecondaryConversionCount", label: "Secondary Web Conversions (lifetime)" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.WEB_CONVERSIONS })),
          ...[
               { value: "lifeTimeSaleConversionCount", label: "All Sales Conversions (lifetime)" },
               { value: "lifeTimeSalePrimaryConversionCount", label: "Primary Sales Conversions (lifetime)" },
               { value: "lifeTimeSaleSecondaryConversionCount", label: "Secondary Sales Conversions (lifetime)" },
          ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.SALE_CONVERSIONS })),
     ],
     propertiesOptional: {
          [PROPERTY_TYPES.NO_OPTIONS]: [
               ...[
                    { value: "lifeTimeSourcePath", label: "Lifetime Source (path)" },
                    { value: "lifeTimeSourcePathND", label: "Lifetime Source (path non-direct)" },
                    { value: "lifeTimeLandingPagePath", label: "Lifetime Landing Page (path)" },
                    { value: "lifeTimeReferrerOriginPath", label: "Lifetime Referrer Origin (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),
          ],
          [PROPERTY_TYPES.REVENUE]: [
               ...[
                    { value: "closedDate", label: "Closed Date", type: "timestamp" },
                    { value: "forecastedClosedDate", label: "Forecast Closed Date", type: "timestamp" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),
               ...[
                    { value: "isWon", label: "Is Won" },
                    { value: "isClosed", label: "Is Closed" },
                    { value: "primarySalesStatus", label: "Primary Sales Status" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "TRXCurrency", label: "TRX Currency" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),
               ...[
                    { value: "closedSourcePath", label: "Closed Source (path)" },
                    { value: "closedSourcePathND", label: "Closed Source (path non-direct)" },
                    { value: "closedLandingPagePath", label: "Closed Landing Page (path)" },
                    { value: "closedReferrerOriginPath", label: "Closed Referrer Origin (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.CREATED_SOURCE })),
          ],
          [PROPERTY_TYPES.COMPANY]: [
               ...[
                    { value: "domain", label: "Domain" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),
               ...[
                    { value: "isCustomer", label: "Is Customer" },
                    { value: "isCustomerDate", label: "Is Customer Date", type: "date" },
                    { value: "isLostCustomer", label: "Is Lost Customer" },
                    { value: "isLostCustomerDate", label: "Is Lost Customer Date", type: "date" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "createdDate", label: "Created Date", type: "timestamp" },
                    { value: "lastModifiedAt", label: "Last Modified", type: "timestamp" },
                    { value: "firstSessionAt", label: "First Session Date", type: "timestamp" },
                    { value: "lastSessionAt", label: "Last Session Date", type: "timestamp" },
                    { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
                    { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),
               ...[
                    { value: "lifeTimeSourcePath", label: "Lifetime Source (path)" },
                    { value: "lifeTimeSourcePathND", label: "Lifetime Source (path non-direct)" },
                    { value: "lifeTimeLandingPagePath", label: "Lifetime Landing Page (path)" },
                    { value: "lifeTimeReferrerOriginPath", label: "Lifetime Referrer Origin (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),
          ],
          [PROPERTY_TYPES.PERSON]: [
               ...[
                    { value: "email", label: "Primary Email" },
                    { value: "phone", label: "Primary Phone" },
                    { value: "domain", label: "Domain" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.RECORD_INFO })),
               ...[
                    { value: "isCustomer", label: "Is Customer" },
                    { value: "isCustomerDate", label: "Is Customer Date", type: "date" },
                    { value: "isLostCustomer", label: "Is Lost Customer" },
                    { value: "isLostCustomerDate", label: "Is Lost Customer Date", type: "date" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.PIPELINE_STATUS })),
               ...[
                    { value: "createdDate", label: "Created Date", type: "timestamp" },
                    { value: "lastModifiedAt", label: "Last Modified", type: "timestamp" },
                    { value: "firstSessionAt", label: "First Session Date", type: "timestamp" },
                    { value: "lastSessionAt", label: "Last Session Date", type: "timestamp" },
                    { value: "dataActionTimestampUTC", label: "Data Action Timestamp UTC", type: "timestamp" },
                    { value: "dataActionTimestamp", label: "Data Action Timestamp", type: "timestamp" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.DATE_ACCOUNT })),
               ...[
                    { value: "lifeTimeSourcePath", label: "Lifetime Source (path)" },
                    { value: "lifeTimeSourcePathND", label: "Lifetime Source (path non-direct)" },
                    { value: "lifeTimeLandingPagePath", label: "Lifetime Landing Page (path)" },
                    { value: "lifeTimeReferrerOriginPath", label: "Lifetime Referrer Origin (path)" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.LIFETIME_SOURCE_SUMMARY })),
          ],
     },
     metricsOptional: {
          [PROPERTY_TYPES.REVENUE]: [
               ...[
                    { value: "TRXAmount", label: "TRX Amount" },
                    { value: "TRXQuantity", label: "TRX Quantity" },
                    { value: "TRXProbability", label: "TRX Probability" },
               ].map((item) => ({ ...item, section: SECTION_OPTION_SEGMENT.VALUES })),
          ],
     }
};

export const OPTIONS_FILTER = [
     {
          value: 'email',
          optionSubFields: [
               { value: 'email', label: 'Email' },
               { value: 'valid', label: 'Valid' },
               { value: 'qualityScore', label: 'Quality Score' },
               { value: 'emailType', label: 'Email Type' },
               { value: 'roleEmail', label: 'Role Email' },
               { value: 'catchAllEmail', label: 'Catch All Email' },
               { value: 'disposableEmail', label: 'Disposable Email' },
               {
                    value: 'allFields', label: 'All Fields', subFields: [
                         { value: 'json', label: 'JSON', description: 'Returns the all sub fields in a JSON format' },
                         { value: 'keyValue', label: 'Key-value Paid', description: 'Returns the all sub fields in a key value pair with each pair on a new row separated by an equal (=) sign.' },
                         { value: 'onlyValue', label: 'One Value Per Line', description: 'Returns only the fields values, each on a separate line.' },
                    ]
               },
          ]
     },
     {
          value: 'primaryEmail',
          optionSubFields: [
               { value: 'email', label: 'Email' },
               { value: 'valid', label: 'Valid' },
               { value: 'qualityScore', label: 'Quality Score' },
               { value: 'emailType', label: 'Email Type' },
               { value: 'roleEmail', label: 'Role Email' },
               { value: 'catchAllEmail', label: 'Catch All Email' },
               { value: 'disposableEmail', label: 'Disposable Email' },
               {
                    value: 'allFields', label: 'All Fields', subFields: [
                         { value: 'json', label: 'JSON', description: 'Returns the all sub fields in a JSON format' },
                         { value: 'keyValue', label: 'Key-value Paid', description: 'Returns the all sub fields in a key value pair with each pair on a new row separated by an equal (=) sign.' },
                         { value: 'onlyValue', label: 'One Value Per Line', description: 'Returns only the fields values, each on a separate line.' },
                    ]
               },
          ]
     },
     {
          value: 'currentCompany',
          optionSubFields: [
               { value: 'companyName', label: 'Company Name' },
               { value: 'domain', label: 'Domain' },
               { value: 'currentPrimaryIndustry', label: 'Current Primary Industry' },
               { value: 'currentRevenue', label: 'Current Revenue' },
               {
                    value: 'allFields', label: 'All Fields', subFields: [
                         { value: 'json', label: 'JSON', description: 'Returns the all sub fields in a JSON format' },
                         { value: 'keyValue', label: 'Key-value Paid', description: 'Returns the all sub fields in a key value pair with each pair on a new row separated by an equal (=) sign.' },
                         { value: 'onlyValue', label: 'One Value Per Line', description: 'Returns only the fields values, each on a separate line.' },
                    ]
               },
          ]
     },
     {
          value: 'emailList',
          optionSubFields: [
               {
                    value: 'allEmails', label: 'All Emails', subFields: [
                         { value: 'commaSeparated', label: 'Comma Separated List', description: 'Returns only the fields values, each on a separate line' },
                         { value: 'json', label: 'JSON', description: 'Returns a simple json array with all values' },
                         { value: 'onlyValue', label: 'One Value Per Line', description: 'Returns one value per line' },
                    ]
               },
               {
                    value: 'allFields', label: 'All Fields', subFields: [
                         { value: 'json', label: 'JSON', description: 'Returns the all sub fields in a JSON format' },
                         { value: 'commaSeparated', label: 'Comma Separated', description: 'Returns a CSV structure where the first row contains the sub fields labels and the second row contains the values.' },
                    ]
               },
          ]
     },
     {
          value: 'relatedCompanies',
          optionSubFields: [
               {
                    value: 'allCompaniesName', label: 'All Companies Name', subFields: [
                         { value: 'commaSeparated', label: 'Comma Separated List', description: 'Returns only the fields values, each on a separate line' },
                         { value: 'json', label: 'JSON', description: 'Returns a simple json array with all values' },
                         { value: 'onlyValue', label: 'One Value Per Line', description: 'Returns one value per line' },
                    ]
               },
               {
                    value: 'allFields', label: 'All Fields', subFields: [
                         { value: 'json', label: 'JSON', description: 'Returns the all sub fields in a JSON format' },
                         { value: 'commaSeparated', label: 'Comma Separated', description: 'Returns a CSV structure where the first row contains the sub fields labels and the second row contains the values.' },
                    ]
               },
          ]
     },
     {
          value: 'enrichTypeValue',
          optionSubFields: [
               { value: 'B2B', label: 'B2B' },
               { value: 'B2C', label: 'B2C' },
               {
                    value: 'allFields', label: 'All Fields', subFields: [
                         { value: 'json', label: 'JSON', description: 'Returns the all sub fields in a JSON format' },
                         { value: 'keyValue', label: 'Key-value Paid', description: 'Returns the all sub fields in a key value pair with each pair on a new row separated by an equal (=) sign.' },
                         { value: 'onlyValue', label: 'One Value Per Line', description: 'Returns only the fields values, each on a separate line.' },
                    ]
               },
          ]
     },
     {
          value: 'otherRoles',
          optionSubFields: [
               {
                    value: 'allTitles', label: 'All Titles', subFields: [
                         { value: 'commaSeparated', label: 'Comma Separated List', description: 'Returns only the fields values, each on a separate line' },
                         { value: 'json', label: 'JSON', description: 'Returns a simple json array with all values' },
                         { value: 'onlyValue', label: 'One Value Per Line', description: 'Returns one value per line' },
                    ]
               },
               {
                    value: 'allFields', label: 'All Fields', subFields: [
                         { value: 'json', label: 'JSON', description: 'Returns the all sub fields in a JSON format' },
                         { value: 'commaSeparated', label: 'Comma Separated', description: 'Returns a CSV structure where the first row contains the sub fields labels and the second row contains the values.' },
                    ]
               },
          ]
     },
     {
          value: 'volunteering',
          optionSubFields: [
               {
                    value: 'allRoles', label: 'All Roles', subFields: [
                         { value: 'commaSeparated', label: 'Comma Separated List', description: 'Returns only the fields values, each on a separate line' },
                         { value: 'json', label: 'JSON', description: 'Returns a simple json array with all values' },
                         { value: 'onlyValue', label: 'One Value Per Line', description: 'Returns one value per line' },
                    ]
               },
               {
                    value: 'allFields', label: 'All Fields', subFields: [
                         { value: 'json', label: 'JSON', description: 'Returns the all sub fields in a JSON format' },
                         { value: 'commaSeparated', label: 'Comma Separated', description: 'Returns a CSV structure where the first row contains the sub fields labels and the second row contains the values.' },
                    ]
               },
          ]
     },
     {
          value: 'certifications',
          optionSubFields: [
               {
                    value: 'allTitles', label: 'All Titles', subFields: [
                         { value: 'commaSeparated', label: 'Comma Separated List', description: 'Returns only the fields values, each on a separate line' },
                         { value: 'json', label: 'JSON', description: 'Returns a simple json array with all values' },
                         { value: 'onlyValue', label: 'One Value Per Line', description: 'Returns one value per line' },
                    ]
               },
               {
                    value: 'allFields', label: 'All Fields', subFields: [
                         { value: 'json', label: 'JSON', description: 'Returns the all sub fields in a JSON format' },
                         { value: 'commaSeparated', label: 'Comma Separated', description: 'Returns a CSV structure where the first row contains the sub fields labels and the second row contains the values.' },
                    ]
               },
          ]
     },
     {
          value: 'education',
          optionSubFields: [
               {
                    value: 'allSchoolNames', label: 'All School Names', subFields: [
                         { value: 'commaSeparated', label: 'Comma Separated List', description: 'Returns only the fields values, each on a separate line' },
                         { value: 'json', label: 'JSON', description: 'Returns a simple json array with all values' },
                         { value: 'onlyValue', label: 'One Value Per Line', description: 'Returns one value per line' },
                    ]
               },
               {
                    value: 'allFields', label: 'All Fields', subFields: [
                         { value: 'json', label: 'JSON', description: 'Returns the all sub fields in a JSON format' },
                         { value: 'commaSeparated', label: 'Comma Separated', description: 'Returns a CSV structure where the first row contains the sub fields labels and the second row contains the values.' },
                    ]
               },
          ]
     },
     {
          value: 'projects',
          optionSubFields: [
               {
                    value: 'allTitles', label: 'All Titles', subFields: [
                         { value: 'commaSeparated', label: 'Comma Separated List', description: 'Returns only the fields values, each on a separate line' },
                         { value: 'json', label: 'JSON', description: 'Returns a simple json array with all values' },
                         { value: 'onlyValue', label: 'One Value Per Line', description: 'Returns one value per line' },
                    ]
               },
               {
                    value: 'allFields', label: 'All Fields', subFields: [
                         { value: 'json', label: 'JSON', description: 'Returns the all sub fields in a JSON format' },
                         { value: 'commaSeparated', label: 'Comma Separated', description: 'Returns a CSV structure where the first row contains the sub fields labels and the second row contains the values.' },
                    ]
               },
          ]
     },
     {
          value: 'recommendations',
          optionSubFields: [
               {
                    value: 'allRecommenderNames', label: 'All Recommender Names', subFields: [
                         { value: 'commaSeparated', label: 'Comma Separated List', description: 'Returns only the fields values, each on a separate line' },
                         { value: 'json', label: 'JSON', description: 'Returns a simple json array with all values' },
                         { value: 'onlyValue', label: 'One Value Per Line', description: 'Returns one value per line' },
                    ]
               },
               {
                    value: 'allFields', label: 'All Fields', subFields: [
                         { value: 'json', label: 'JSON', description: 'Returns the all sub fields in a JSON format' },
                         { value: 'commaSeparated', label: 'Comma Separated', description: 'Returns a CSV structure where the first row contains the sub fields labels and the second row contains the values.' },
                    ]
               },
          ]
     },
]